/**
 * =============================================================================
 * IDS CRM Web - Task Detail Page
 * =============================================================================
 *
 * PURPOSE:
 * View and manage a single scheduled task (engineer work) with work logs,
 * assignment history and notes. Laid out to match TicketDetail exactly so the
 * three work streams (reactive / scheduled work / scheduled maintenance) read
 * as one system.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import BackToList, { useBackToList } from '../components/BackToList';
import DeleteRecordButton, { canDeleteRecords } from '../components/DeleteRecordButton';
import { useAuth } from '../context/AuthContext';
import { filesToAttachmentPayload, savedAttachmentsOnly } from '../utils/attachments';
import { authedFileHref } from '../utils/files';
import { validateAnswers } from '../components/TemplateCheckFormRenderer';
import DailyVanCheckSection, {
  emptyDailyVanCheck, validateDailyVanCheck
} from '../components/DailyVanCheckSection';
import JobStagesCard, { ARRIVED_HOME_LABEL, FINISH_STAGES_HINT, jobStagesProblem } from '../components/JobStagesCard';
import WorkLogOutcomeSection, { outcomeClosesWork, CARRY_ON_NOTES_NEEDED } from '../components/WorkLogOutcomeSection';
import TaskAssetCheckSection, { emptyRobotOutcome, emptyRobotFollowUp, savedFormMatches } from '../components/TaskAssetCheckSection';
import EngineerPicker from '../components/EngineerPicker';
import ConfirmHoursModal from '../components/ConfirmHoursModal';
import { useConfirm } from '../components/ConfirmDialog';
import {
  SavedLogWorkPanel, ClearLogWorkModal, continueSubtitle, ukDayKeyOf, ukDayLabelOfKey, ukWeekdayOfKey,
  whenText, listJoin, firstNameOf, partLineLabel
} from '../components/SavedLogWorkPanel';
import useSavedLogWork, {
  NO_SIGNAL_SAVE_MESSAGE, OFFLINE_OPEN_MESSAGE, SEND_FAILED_MESSAGE, logWorkAtWhen, newUuid, staleSpareLines
} from '../hooks/useSavedLogWork';
import { attachmentFromFileRef, linkSentAttachments, uploadFilesOneByOne } from '../hooks/useTemplateCheckForm';
import { buildLogWorkPayload } from '../utils/logWorkPayload';
import {
  isOperationalReadOnly, canMoveDealStage, getCapabilities, canClearLogWork, canFitParts, canCorrectParts
} from '../utils/permissions';
import SparePartsUsed from '../components/SparePartsUsed';
import PartsUsedList, { readPartsUsed } from '../components/PartsUsedList';
import WorkLogFormViewer, { WorkLogFormList } from '../components/WorkLogFormViewer';
import {
  reopenActions, cancelReopenRequest, ReopenStatusLine, WorkLogChanges,
  RequestReopenModal, DecideReopenModal, ReopenLogModal, UpdateWorkLogModal,
  SAVED_STILL_REOPENED_MESSAGE, COMPLETED_AGAIN_MESSAGE
} from '../components/WorkLogReopen';
import { useSparePartsList } from '../utils/sparePartsList';
import { firstSpareProblem, partsSummaryForLog, quantityText, spareLineLabel } from '../utils/spareLines';
import { formatDateTime, formatTime, toLocalDateTimeInput } from '../utils/format';
import { formatDateKey, ukTodayKey } from '../utils/dates';
import {
  liveReturnVisits, returnVisitCovering, bookedOnSentence, shortDayLabel, ticketStatusLabel
} from '../utils/returnVisits';
import ReviewNotes from '../components/ReviewNotes';
import './TaskDetail.css';
import OpsBoardServiceStatus from '../components/OpsBoardServiceStatus';

// A job is opened from Scheduling and from Jobs & Tickets: its "←" link goes
// back to the list it was opened from, as that list was left.
const JOB_LISTS = { '/tasks': 'Scheduling', '/tickets': 'Jobs & Tickets' };

// Quarterly Health Check and Annual Service are named as New Work names them
// (TaskForm), so a maintenance visit reads the same on the form that made it
// and the page that shows it. The other labels predate New Work's wording.
function getTaskTypeLabel(type) {
  switch (type) {
    case 'quarterly_health_check': return 'Quarterly Health Check';
    case 'annual_service': return 'Annual Service';
    case 'remote_check_in': return 'Remote Check-in';
    case 'upgrade': return 'Upgrade';
    case 'drop_in': return 'Drop-in Visit';
    case 'sales_action': return 'Sales Task';
    default: return type?.replace(/_/g, ' ');
  }
}

/**
 * The robots on a job, primary first (multi-asset jobs, Phase 3). The list
 * comes from task.assets; an older fixture with no list falls back to the
 * job's single asset, read as done once the job is complete; a job with no
 * robot gives an empty list.
 */
function robotsOnJob(task) {
  const listed = (Array.isArray(task?.assets) ? task.assets : []).filter(row => row && row.asset_id);
  if (listed.length > 0) {
    // A job completed before per-robot sign-off existed has its robots listed
    // with no visit outcome (the Phase 1 backfill copied the health score only),
    // so a complete job reads every unsettled robot as done, with the job's
    // health where the row has none. A cancelled job leaves them unsettled and
    // the chip shows nothing for them.
    const settled = task?.status === 'complete'
      ? listed.map(row => (row.visit_outcome ? row : {
        ...row,
        visit_outcome: 'done',
        health_score_recorded: row.health_score_recorded || task.health_score_recorded || null
      }))
      : listed;
    return [...settled].sort((a, b) => {
      const aPrimary = a.is_primary === true || a.asset_id === task?.asset_id;
      const bPrimary = b.is_primary === true || b.asset_id === task?.asset_id;
      return (bPrimary ? 1 : 0) - (aPrimary ? 1 : 0);
    });
  }
  if (task?.asset_id) {
    return [{
      asset_id: task.asset_id,
      asset_ref: task.asset_ref || null,
      robot_type: task.robot_type || null,
      serial_number: task.serial_number || null,
      health_score_recorded: task.health_score_recorded || null,
      visit_outcome: task.status === 'complete' ? 'done' : null,
      outcome_note: null,
      is_primary: true
    }];
  }
  return [];
}

const PLANNING_GROUP_LABEL = 'Maintenance Planning Owner group';

const engineerNameOf = (engineer) => engineer?.full_name || engineer?.preferred_name || engineer?.email || 'Unknown engineer';

/**
 * The engineers booked on a job, lead first (several engineers on one job,
 * Phase 5). The list comes from task.engineers; an older API with no list
 * falls back to the job's single assignee; a group job, or an unassigned one,
 * gives an empty list.
 */
function engineersOnJob(task) {
  const listed = (Array.isArray(task?.engineers) ? task.engineers : []).filter(row => row && row.user_id);
  if (listed.length > 0) {
    const isLead = (row) => row.is_lead === true || (Boolean(task?.assigned_to_id) && row.user_id === task.assigned_to_id);
    return [...listed].sort((a, b) => (isLead(b) ? 1 : 0) - (isLead(a) ? 1 : 0));
  }
  if (task?.assigned_to_id) {
    return [{ user_id: task.assigned_to_id, full_name: task.assigned_to_name || null, is_lead: true }];
  }
  return [];
}

/**
 * Where one engineer's hours on this job stand, from the job's work logs: an
 * open log of theirs (a crew entry not yet signed off) is 'Hours to confirm',
 * otherwise a closed one is 'Hours logged', otherwise nothing is logged yet.
 * Being booked on a job never creates hours - only a work log does.
 */
function crewHoursStatus(userId, logs, unfinishedIds = null) {
  const theirs = (Array.isArray(logs) ? logs : []).filter(log => log.engineer_id === userId);
  if (theirs.some(log => !log.outcome)) return { key: 'to-confirm', label: 'Hours to confirm' };
  if (theirs.length > 0) return { key: 'logged', label: 'Hours logged' };
  // Saved Log Work: Job Stages in no hours yet, or in a Log Work nobody has
  // saved as hours (spec 6.4, the crew card).
  if (unfinishedIds && unfinishedIds.has(String(userId || '').toLowerCase())) {
    return { key: 'unfinished', label: 'Log Work not finished' };
  }
  return { key: 'none', label: 'Nothing logged yet' };
}

// ---------------------------------------------------------------------------
// Saved Log Work (Phase 6 step 7, spec 6.4)
// ---------------------------------------------------------------------------

const lowerId = (value) => String(value ?? '').trim().toLowerCase();

/** The saved Log Work part a check-form section saves into. */
const partKeyOfSection = (sectionKey) => (sectionKey === 'task' ? 'form:task' : `robot:${lowerId(sectionKey)}`);
const isFormPartKey = (key) => String(key).startsWith('robot:') || key === 'form:task';

const hasFormAnswers = (value) => Boolean(value && value.form && value.form.answers
  && typeof value.form.answers === 'object' && Object.keys(value.form.answers).length > 0);

/** A van check as its saved part holds it: the photos are files of their own. */
const withoutPhotos = (check) => {
  const { damage_photo_attachments: photos, ...rest } = check || {};
  return rest;
};

// The refusals Complete Work Log can meet because the saved Log Work moved on.
const LOG_WORK_FILING_CODES = [
  'LOG_WORK_CHANGED', 'LOG_WORK_ALREADY_SAVED', 'LOG_WORK_CLEARED', 'LOG_WORK_IN_PROGRESS',
  'LOG_WORK_NEXT_STEP', 'LOG_WORK_CREW_MISSING', 'LOG_WORK_UNFILED_JOURNEY'
];

export const CARRY_ON_SAVED_MESSAGE = "Today's hours are saved. Your robots' answers are kept for tomorrow in Continue Log Work. Choose the work period again tomorrow.";
// Finish at home (Gareth 2026-10-12): everything is filled in on site and
// kept as saved Log Work; it is completed once the last Job Stages time is
// recorded, from Today.
export const FINISH_AT_HOME_SAVED_MESSAGE = `Saved, not hours yet. When you are home, or leave for your next job, go to Today to record ${ARRIVED_HOME_LABEL} and complete this Log Work.`;
export const JOB_STAGES_STILL_LOADING_MESSAGE = 'Your Job Stages times are still loading. Save again when they are shown.';

// ---------------------------------------------------------------------------
// Parts used (spare parts, Push 2, spec 6.4, 6.5 and 8.5)
// ---------------------------------------------------------------------------

export const SPARE_PARTS_UNAVAILABLE_MESSAGE = 'Parts used could not be sent because saved Log Work is not available. They are kept on this phone. Save again in a few minutes.';
export const SPARE_PARTS_LOCKED_MESSAGE = 'Parts used cannot be changed right now because saved Log Work is not available. Try again shortly.';

const isSparePartKey = (key) => String(key).startsWith('spare:');

/** Does this saved Log Work view (the hook's merged view) hold a Parts used line? */
const holdsSpareLines = (view) => Object.entries((view && view.parts) || {})
  .some(([key, part]) => isSparePartKey(key) && part && part.value);

const timeOrZero = (value) => {
  const ms = value ? new Date(value).getTime() : NaN;
  return Number.isNaN(ms) ? 0 : ms;
};

/** Parts used lines in the order they were added (then by key, so the list never jumps). */
const byAddedAt = (a, b) => (timeOrZero(a.value.added_at) - timeOrZero(b.value.added_at)) || a.key.localeCompare(b.key);

const linesPhrase = (names) => {
  const n = names.length;
  return { list: listJoin(names), was: n === 1 ? 'was' : 'were', itWas: n === 1 ? 'it was' : 'they were', it: n === 1 ? 'it' : 'them' };
};

/** After Complete Work Log: lines the filing skipped because an earlier Log Work recorded them (6.3). */
function spareAlreadyRecordedNotice(names) {
  const words = linesPhrase(names);
  return `${words.list} ${words.was} already recorded with an earlier Log Work, so ${words.itWas} not recorded again.`;
}

/** After Copy into today's Log Work: lines copied under their own keys (6.5). */
function spareCopiedNotice(names) {
  const words = linesPhrase(names);
  return `${words.list} ${words.was} copied into today's Log Work. If ${words.itWas} already recorded, saving will not record ${words.it} twice.`;
}

/**
 * Q3 mirror (spec 5.2 / 6.4): an open entry blocks filing again only on the
 * UK days it touches. True when the log's span (an open log with no finish
 * counts as its start minute) touches today's UK day.
 */
export function touchesUkToday(log, now = new Date()) {
  const start = new Date(log && log.started_at);
  if (Number.isNaN(start.getTime())) return true; // cannot read it: keep it blocking, as the server does
  const endValue = log && log.ended_at ? new Date(log.ended_at) : new Date(start.getTime() + 60000);
  const end = Number.isNaN(endValue.getTime()) ? new Date(start.getTime() + 60000) : endValue;
  const today = ukTodayKey(now);
  const first = ukTodayKey(start);
  const last = ukTodayKey(new Date(Math.max(start.getTime(), end.getTime() - 1)));
  return first <= today && today <= last;
}

/**
 * Who gets Log Work and Confirm my hours on a job (Phase 4, with the Q3
 * mirror once the saved Log Work is live).
 */
function logWorkAccessFor({ task, user, readOnlyWorkspace, savedLive }) {
  const isOpen = !['complete', 'cancelled'].includes(task?.status);
  const workLogs = Array.isArray(task?.work_logs) ? task.work_logs : [];
  const myLogs = user?.userId ? workLogs.filter(log => log.engineer_id === user.userId) : [];
  // The viewer's own entry from someone else's crew, not yet signed off.
  // Offered as Confirm my hours instead of Log Work - even on a finished job -
  // so nobody files the same visit twice.
  const myOpenLog = myLogs.find(log => isPeerLog(log) && !log.outcome) || null;
  // Q3 (spec 5.2): with the saved Log Work live, that open entry hides Log
  // Work only on the UK day it touches; another day's visit can still be
  // logged, and Confirm my hours stays offered. Before release, as before.
  const openLogBlocks = Boolean(myOpenLog) && (!savedLive || touchesUkToday(myOpenLog));
  // A signed-off entry from someone else's crew: Log Work is hidden so that
  // visit is not filed twice. Only a crew entry counts - the viewer's own
  // handover logs never hide it, because a job they come back to on another
  // day still needs their log. The job's own engineers (the assignee and
  // everyone on its engineer list) always keep it; the server's overlap
  // guard refuses a second filing for the same time either way.
  const isAssignee = Boolean(user?.userId) && task?.assigned_to_id === user.userId;
  const onJob = isAssignee
    || (Boolean(user?.userId) && (Array.isArray(task?.engineers) ? task.engineers : [])
      .some(engineer => engineer.user_id === user.userId));
  const mySignedOff = !myOpenLog && !onJob && myLogs.some(log => isPeerLog(log) && Boolean(log.outcome));
  const canLogWork = !readOnlyWorkspace && isOpen && !openLogBlocks && !mySignedOff;
  return { myOpenLog, mySignedOff, canLogWork, onJob };
}

/**
 * Crew logs on a job (Phase 4, Gareth's 11 Sep crew rules): a log EACH,
 * linked by work_log_group_id, where the group id IS the lead's log id.
 */
const isPeerLog = (log) => Boolean(log?.work_log_group_id) && log.work_log_group_id !== log.log_id;

/**
 * The work logs in page order with each crew kept together: the lead first,
 * then the rest of the crew, at the place the first of them appears. Logs
 * outside a crew keep their order.
 */
function groupCrewLogs(logs) {
  const rows = Array.isArray(logs) ? logs : [];
  const placed = new Set();
  const ordered = [];
  rows.forEach(log => {
    if (placed.has(log.log_id)) return;
    if (!log.work_log_group_id) {
      placed.add(log.log_id);
      ordered.push(log);
      return;
    }
    const crew = rows.filter(member => member.work_log_group_id === log.work_log_group_id);
    const lead = crew.filter(member => member.log_id === log.work_log_group_id);
    const rest = crew.filter(member => member.log_id !== log.work_log_group_id);
    [...lead, ...rest].forEach(member => {
      if (placed.has(member.log_id)) return;
      placed.add(member.log_id);
      ordered.push(member);
    });
  });
  return ordered;
}

/**
 * The state chip for one robot on the job page: Outstanding, Done (with its
 * health) or Not done. A robot never settled on a cancelled job gets no chip:
 * nothing was outstanding once the job was called off.
 */
function RobotStateChip({ robot, jobStatus }) {
  if (robot.visit_outcome === 'done') {
    const health = robot.health_score_recorded || null;
    return (
      <span className="robot-state robot-state-done" title={health ? `Health: ${health}` : undefined}>
        {health && <span className={`robot-health-dot health-${health}`} aria-label={`Health ${health}`} />}
        Done
      </span>
    );
  }
  if (robot.visit_outcome === 'not_done') {
    return <span className="robot-state robot-state-not-done">Not done</span>;
  }
  if (jobStatus === 'cancelled') return null;
  return <span className="robot-state robot-state-outstanding">Outstanding</span>;
}

function TaskDetail() {
  const { id } = useParams();
  const location = useLocation();
  const { authFetch, user } = useAuth();
  const readOnlyWorkspace = isOperationalReadOnly(user?.role);
  // Team leaders and up book, change and cancel a job (Gareth 2026-09-23);
  // engineers start it, log it and name who worked with them.
  const canSchedule = !readOnlyWorkspace && Boolean(getCapabilities(user).schedule_work);
  // Engineers receive sales_action tasks but cannot open the opportunity
  // behind them, so they get the reference as plain text rather than a link
  // to a page that 403s. Their context is the brief on the task itself.
  const canOpenDeals = canMoveDealStage(user?.role);

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // A link ending ?tab=work (a message about a work log, a row on Today or
  // Approvals) opens the job on its Work Logs tab.
  const linkedToWorkTab = new URLSearchParams(location.search).get('tab') === 'work';
  const [activeTab, setActiveTab] = useState(linkedToWorkTab ? 'work' : 'details');
  useEffect(() => {
    if (linkedToWorkTab) setActiveTab('work');
  }, [id, linkedToWorkTab]);
  const [showWorkLogModal, setShowWorkLogModal] = useState(false);
  // Reopening a completed work log to update it (Gareth 2026-10-13, jobs
  // 2026-10-14): the window open on one log ({ kind, log }), what the last
  // step said, and a count that makes an open Changes list read again.
  const [reopenWindow, setReopenWindow] = useState(null);
  const [reopenMessage, setReopenMessage] = useState(null);
  const [changesKey, setChangesKey] = useState(0);
  // B4: what the API says about a call-out saved into a month that has already
  // been paid - shown to the engineer who saved it, not swallowed.
  const [payrollNotice, setPayrollNotice] = useState(null);
  // Phase 6 step 4: the follow-up tickets the last Log Work raised, one per
  // faulty robot, named once the save is done.
  const [raisedFollowUps, setRaisedFollowUps] = useState([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  // Delete (managers and admins, Gareth 2026-10-13): after it, back to the
  // list as it was left; when the server refuses, its reason is shown.
  const backToList = useBackToList('/tasks', ['/tickets']);
  const [deleteRefused, setDeleteRefused] = useState(null);
  const [engineers, setEngineers] = useState([]);
  const [workLogConfig, setWorkLogConfig] = useState({ work_types: [], work_periods: [], outcomes: [] });
  const [cancelReason, setCancelReason] = useState('');
  // Phase 4: the co-engineer's own entry being confirmed, and the lead log
  // whose crew a manager is correcting.
  const [confirmLog, setConfirmLog] = useState(null);
  const [crewModalLog, setCrewModalLog] = useState(null);
  // Saved Log Work: Change crew opened from the clear window, with the
  // engineer whose Job Stages are in no hours already ticked.
  const [crewModalPreset, setCrewModalPreset] = useState(null);
  const [showClearModal, setShowClearModal] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  // The viewer's own Job Stages on this job, for Confirm my hours.
  const [myJourneys, setMyJourneys] = useState([]);

  // Phase 6 step 7: the job's saved Log Work, one per visit, shared by the
  // crew. Only Complete Work Log makes hours. Before the store is released every
  // answer is "unavailable" and the page behaves exactly as before.
  const assetRefs = useMemo(() => Object.fromEntries(robotsOnJob(task)
    .filter(robot => robot.asset_id && robot.asset_ref)
    .map(robot => [lowerId(robot.asset_id), robot.asset_ref])), [task]);
  const savedLogWork = useSavedLogWork({
    authFetch,
    taskId: id,
    userId: user?.userId || null,
    enabled: Boolean(id && user?.userId),
    userName: user?.fullName || user?.full_name || null,
    assetRefs
  });
  const savedLive = savedLogWork.status === 'ready' || savedLogWork.status === 'offline';

  // Spare parts (Push 2, spec 6.4 and 8.5): the phone's copy of the parts
  // list, fetched here while there is signal, for the Log Work picker and
  // Add a part; and the job's Parts used list for the Work Logs tab.
  const spareParts = useSparePartsList({ authFetch, enabled: Boolean(user && canFitParts(user.role)) });
  const [parts, setParts] = useState([]);
  // 'loading' while the latest read is out, then 'ready' or 'failed'. Only
  // the latest read counts, so an older answer never replaces a newer one.
  const [partsStatus, setPartsStatus] = useState('loading');
  const partsReadRef = useRef(0);
  // Every page of the job's parts, not only the first 100: a line is known
  // to be recorded only once the whole list has been read.
  const fetchParts = useCallback(async () => {
    const read = partsReadRef.current + 1;
    partsReadRef.current = read;
    setPartsStatus('loading');
    const rows = await readPartsUsed(authFetch, { taskId: id });
    if (read !== partsReadRef.current) return;
    if (rows) setParts(rows);
    setPartsStatus(rows ? 'ready' : 'failed');
  }, [authFetch, id]);
  useEffect(() => {
    fetchParts();
  }, [fetchParts]);
  // A read that failed is tried again when the phone is back online.
  const partsStatusRef = useRef(partsStatus);
  partsStatusRef.current = partsStatus;
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const online = () => { if (partsStatusRef.current === 'failed') fetchParts(); };
    window.addEventListener('online', online);
    return () => window.removeEventListener('online', online);
  }, [fetchParts]);

  // Parts used this phone could not record because the job was closed first
  // (spec 6.5): its stale copies' lines, shown on the job page because Log
  // Work cannot open on a finished job. A line the job's Parts used list
  // holds (its save reached the server, the reply was lost, and the lead's
  // save recorded it) was recorded after all: it is named once and never
  // offered again, and a copy with nothing else missing is discarded.
  const jobFinishedLive = savedLive && savedLogWork.jobFinished;
  const savedStaleCopies = savedLogWork.staleCopies;
  const resolveStaleCopy = savedLogWork.resolveStale;
  const staleSpareCopies = useMemo(() => {
    if (!jobFinishedLive) return [];
    const recordedIds = new Set(parts.map(row => lowerId(row.client_line_id)).filter(Boolean));
    return (Array.isArray(savedStaleCopies) ? savedStaleCopies : [])
      .map(copy => {
        const lines = staleSpareLines(copy);
        const recorded = lines.filter(line => recordedIds.has(lowerId(line.key.slice('spare:'.length))));
        return { id: copy.id, lines, recorded, missing: lines.filter(line => !recorded.includes(line)) };
      })
      .filter(copy => copy.lines.length > 0);
  }, [jobFinishedLive, savedStaleCopies, parts]);
  // The job was closed while this page was open: read its Parts used again,
  // so a line the closing save recorded is not listed as missing.
  useEffect(() => {
    if (jobFinishedLive) fetchParts();
  }, [jobFinishedLive, fetchParts]);
  const [recordedAfterAll, setRecordedAfterAll] = useState([]);
  const announcedSparesRef = useRef(new Set());
  const discardedCopiesRef = useRef(new Set());
  // Only on the job's Parts used as just read: a line is never named as
  // recorded, or its copy thrown away, on a list that may be out of date.
  const partsReady = partsStatus === 'ready';
  useEffect(() => {
    if (!partsReady) return;
    const names = [];
    staleSpareCopies.forEach(copy => {
      copy.recorded.forEach(line => {
        if (announcedSparesRef.current.has(line.key)) return;
        announcedSparesRef.current.add(line.key);
        names.push(spareLineLabel(line.name, line.itemNumber));
      });
      if (copy.missing.length === 0 && !discardedCopiesRef.current.has(copy.id)) {
        discardedCopiesRef.current.add(copy.id);
        resolveStaleCopy(copy.id, 'discard');
      }
    });
    if (names.length > 0) setRecordedAfterAll(prev => [...prev, ...names]);
  }, [partsReady, staleSpareCopies, resolveStaleCopy]);
  const { confirmElement, requestConfirm } = useConfirm();
  const discardStaleSpares = (copy) => requestConfirm({
    title: 'Discard these parts?',
    message: 'They will not be recorded on the job.',
    confirmLabel: 'Discard',
    danger: true,
    onConfirm: () => { resolveStaleCopy(copy.id, 'discard'); }
  });

  // The forms filled in on this job's work logs (robot checks, van checks,
  // site forms), listed under each log, and the one open in the viewer. A
  // read that fails leaves the list as it was: the page never depends on it.
  const [logForms, setLogForms] = useState([]);
  const [openFormId, setOpenFormId] = useState(null);
  const fetchLogForms = useCallback(async () => {
    try {
      const response = await authFetch(`/api/v1/work-logs/forms?task_id=${id}`);
      if (!response || !response.ok) return;
      const data = await response.json();
      if (Array.isArray(data?.data)) setLogForms(data.data);
    } catch (err) {
      // Kept as it was.
    }
  }, [authFetch, id]);

  const fetchTask = useCallback(async () => {
    try {
      const response = await authFetch(`/api/v1/tasks/${id}`);
      if (!response.ok) {
        throw new Error('Task not found');
      }
      const data = await response.json();
      setTask(data);
      fetchLogForms();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, id, fetchLogForms]);

  useEffect(() => {
    fetchTask();
  }, [fetchTask]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [configRes, engineersRes] = await Promise.all([
          authFetch('/api/v1/support/work-log-config'),
          authFetch('/api/v1/tickets/active-engineers')
        ]);
        if (configRes.ok) setWorkLogConfig(await configRes.json());
        if (engineersRes.ok) {
          const data = await engineersRes.json();
          setEngineers(data.data || []);
        }
      } catch (err) {
        console.error('Failed to load work log config:', err);
      }
    };
    fetchConfig();
  }, [authFetch]);

  const startTask = async () => {
    try {
      await authFetch(`/api/v1/tasks/${id}/start`, { method: 'PUT' });
      fetchTask();
    } catch (err) {
      console.error('Failed to start task:', err);
    }
  };

  // The server refuses a cancel while someone has unfinished Log Work on the
  // job; its sentence is shown in the window instead of closing it silently.
  const cancelTask = async () => {
    setCancelError(null);
    try {
      const response = await authFetch(`/api/v1/tasks/${id}/cancel`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reschedule_reason: cancelReason })
      });
      if (response && response.ok === false) {
        const data = await response.json().catch(() => ({}));
        setCancelError(data.message || 'The job could not be cancelled. Try again.');
        return;
      }
      setShowCancelModal(false);
      fetchTask();
    } catch (err) {
      console.error('Failed to cancel task:', err);
      setCancelError('The job could not be cancelled. Check your signal and try again.');
    }
  };

  // ?logwork=1 (Today's "Log Work to finish", the reminders): open Log Work
  // once the job and its saved Log Work are known. Someone who cannot log
  // work here (a team leader on a finished job) is shown the Work Logs tab,
  // where the unfinished Log Work is.
  const logWorkLinkHandledRef = useRef(false);
  useEffect(() => {
    if (logWorkLinkHandledRef.current || !task || savedLogWork.status === 'loading') return;
    const wanted = new URLSearchParams(location.search || '').get('logwork') === '1';
    logWorkLinkHandledRef.current = true;
    if (!wanted) return;
    const access = logWorkAccessFor({ task, user, readOnlyWorkspace, savedLive });
    if (access.canLogWork) setShowWorkLogModal(true);
    else setActiveTab('work');
  }, [task, savedLogWork.status, location.search, user, readOnlyWorkspace, savedLive]);

  // Confirm my hours offers the viewer's own Job Stages times on this job
  // (question 1: a crew entry keeps the filer's times).
  useEffect(() => {
    if (!confirmLog) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await authFetch(`/api/v1/travel?task_id=${encodeURIComponent(id)}&my=true&limit=50`);
        if (!response || !response.ok) return;
        const data = await response.json().catch(() => ({}));
        if (!cancelled) setMyJourneys(Array.isArray(data.data) ? data.data : []);
      } catch (err) {
        // The button is only a help; without it the times are typed.
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch, id, confirmLog]);

  const getTaskTypeClass = (type) => {
    switch (type) {
      case 'quarterly_health_check': return 'type-qhc';
      case 'annual_service': return 'type-annual';
      case 'remote_check_in': return 'type-remote';
      case 'upgrade': return 'type-upgrade';
      case 'drop_in': return 'type-dropin';
      case 'sales_action': return 'type-sales';
      default: return 'type-default';
    }
  };

  // scheduled_date is a calendar day: read it as one, so this page names the
  // same day as the schedule it was opened from (new Date() on the API's
  // UTC-midnight value showed the day before west of UTC).
  const formatScheduleDate = (dateStr) => formatDateKey(dateStr, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });

  const formatScheduleTime = (time) => {
    if (!time) return 'All day';
    return time.substring(0, 5);
  };

  const formatDuration = (mins) => {
    if (!mins) return '-';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h} hr`;
    return `${h} hr ${m} min`;
  };

  if (loading) {
    return <div className="loading-page">Loading task...</div>;
  }

  if (error) {
    return (
      <div className="error-page">
        <h2>Error</h2>
        <p>{error}</p>
        <BackToList to="/tasks" also={['/tickets']} labels={JOB_LISTS}>← Scheduling</BackToList>
      </div>
    );
  }

  const isOpen = !['complete', 'cancelled'].includes(task.status);
  // Phase 3: every robot on the job, primary first, for the header and the
  // robots card.
  const robots = robotsOnJob(task);
  const extraRobotCount = Math.max(robots.length - 1, 0);
  const allRobotRefs = robots.map(robot => robot.asset_ref || robot.asset_id).join(', ');
  // Phase 5: every engineer booked on the job, lead first, for the header
  // and the assignment card.
  const crew = engineersOnJob(task);
  const extraEngineerCount = Math.max(crew.length - 1, 0);
  const allEngineerNames = crew.map(engineerNameOf).join(', ');
  const unassignedLabel = task.assigned_group ? PLANNING_GROUP_LABEL : 'Unassigned';

  // Phase 4 - a log each on jobs. These mirror the server (routes/tasks.js
  // and services/workLogLock.js), which is the authority and refuses anyway;
  // hiding a button that would only be refused is kinder than showing it.
  // Gareth 2026-09-23: team leaders and up change a logged job's crew
  // (mirrors ROLES.CREW_CHANGE).
  const canCorrectCrew = ['team_leader', 'manager', 'admin'].includes(user?.role);
  const workLogs = Array.isArray(task.work_logs) ? task.work_logs : [];
  const { myOpenLog, mySignedOff, canLogWork } = logWorkAccessFor({ task, user, readOnlyWorkspace, savedLive });

  // Saved Log Work on the page (spec 6.4).
  const savedDraft = savedLive ? savedLogWork.draft : null;
  const continueLogWork = savedLive && savedLogWork.hasContent;
  const continueNote = continueLogWork ? continueSubtitle(savedLogWork) : '';
  const namedByColleague = savedLive && savedLogWork.namesYou
    ? ((savedLogWork.parts.crew && savedLogWork.parts.crew.changed_by_name) || 'A colleague')
    : null;
  const unfiledJourneys = savedLive ? savedLogWork.unfiled : [];
  // Engineers with Job Stages in no hours yet, or in the saved Log Work.
  const unfinishedIds = new Set(savedLive
    ? [...unfiledJourneys.map(row => lowerId(row.engineer_id)), ...savedLogWork.journeys.map(row => lowerId(row.user_id))]
    : []);
  const cancelBlockedBy = savedLive && (savedLogWork.hasContent || unfiledJourneys.length > 0)
    ? ((savedDraft && savedDraft.has_content && savedDraft.last_changed_by_name)
      || (unfiledJourneys[0] && unfiledJourneys[0].full_name)
      || 'Someone')
    : null;
  const canClearHere = savedLive && savedLogWork.canClear && canClearLogWork(user?.role) && !readOnlyWorkspace;
  const crewFor = (log) => {
    if (!log.work_log_group_id) return [log];
    return workLogs.filter(member => member.work_log_group_id === log.work_log_group_id);
  };
  // Crew changes on a JOB are made afterwards with a written reason, and
  // allowed on a closed lead log: a job's lead log is born closed, so the
  // ticket rule ("while the lead's log is open") would never allow one (agreed
  // departure). Team leaders and managers.
  const canChangeCrew = (log) => {
    if (readOnlyWorkspace || !canCorrectCrew) return false;
    return log.work_log_group_id ? log.log_id === log.work_log_group_id : true;
  };
  const canConfirmLog = (log) => !readOnlyWorkspace
    && Boolean(user?.userId) && log.engineer_id === user.userId
    && isPeerLog(log) && !log.outcome;
  // Reopening a completed log to update it: which buttons this person gets.
  const reopenFor = (log) => reopenActions(log, user, { readOnly: readOnlyWorkspace });
  const logPath = (log) => `/api/v1/tasks/${id}/work-log/${log.log_id}`;
  const reopenStepDone = (message) => {
    setReopenWindow(null);
    setReopenMessage(message);
    setChangesKey(count => count + 1);
    fetchTask();
    // A part added to a reopened log shows in the job's Parts used.
    fetchParts();
  };
  const cancelReopen = (log) => requestConfirm({
    title: 'Cancel this request?',
    message: 'Your request to reopen this work log will be withdrawn. You can ask again.',
    confirmLabel: 'Cancel request',
    cancelLabel: 'Keep it',
    onConfirm: async () => {
      try {
        await cancelReopenRequest(authFetch, logPath(log), log.reopen);
        reopenStepDone('Your request to reopen the work log was cancelled.');
      } catch (err) {
        setReopenMessage(err.message);
        fetchTask();
      }
    }
  });
  // The log that completed the job carries its completion summary; a crew
  // member's own entry takes the lead's outcome but never the summary.
  const completedTheJob = (log) => !isPeerLog(log) && task?.status === 'complete'
    && outcomeClosesWork(workLogConfig, log.outcome);
  const leadNameFor = (log) => (log?.work_log_group_id
    ? workLogs.find(member => member.log_id === log.work_log_group_id)?.engineer_name || null
    : null);

  // Phase 6 step 5: a finished job with robots left Not done can have a
  // return visit booked for them. Team leaders and up book it (as they book
  // any job); a sales job is worked from the deal, so it has none. Once every
  // not-done robot is on a return visit, the button becomes a link to it.
  // A not-done robot that has since moved to another customer cannot be
  // booked from this job (the form leaves it out and a return visit must be
  // for the same customer), so it neither offers the button nor keeps it
  // from becoming a link.
  const notDoneRobots = task.status === 'complete'
    ? robots.filter(robot => robot.visit_outcome === 'not_done'
      && (!robot.customer_id || !task.customer_id || robot.customer_id === task.customer_id))
    : [];
  const returnVisits = liveReturnVisits(task);
  const offersReturnVisit = canSchedule && task.status === 'complete'
    && task.task_type !== 'sales_action' && notDoneRobots.length > 0;
  // The return visit each not-done robot is booked on, by asset_id.
  const bookedVisitOf = Object.fromEntries(notDoneRobots
    .map(robot => [robot.asset_id, returnVisitCovering(task, robot.asset_id)])
    .filter(([, visit]) => visit));
  const everyNotDoneBooked = notDoneRobots.length > 0
    && notDoneRobots.every(robot => bookedVisitOf[robot.asset_id]);
  const coveringVisits = returnVisits.filter(visit => Object.values(bookedVisitOf)
    .some(booked => booked.task_id === visit.task_id));

  return (
    <div className="task-detail">
      {/* Header Card */}
      <div className="detail-header">
        <div className="header-left">
          <div className="header-info">
            <BackToList to="/tasks" also={['/tickets']} labels={JOB_LISTS} className="back-link">← Scheduling</BackToList>
            <div className="header-row-1">
              <h1>{task.task_ref}</h1>
              <span className={`type-chip ${getTaskTypeClass(task.task_type)}`}>
                {getTaskTypeLabel(task.task_type)}
              </span>
              <span className={`status-chip status-${task.status}`}>
                {task.status === 'complete' ? 'Completed' : task.status.replace(/_/g, ' ')}
              </span>
            </div>
            <div className="header-row-2">
              <span className="meta-chip">{formatScheduleDate(task.scheduled_date)}</span>
              {task.scheduled_start_time && (
                <span className="meta-chip">{formatScheduleTime(task.scheduled_start_time)}</span>
              )}
              {task.suggested_by_system && <span className="meta-chip">System suggested</span>}
            </div>
            <div className="header-row-3">
              <span className="meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4z"/>
                </svg>
                <Link to={`/customers/${task.customer_id}`} className="customer-link">{task.customer_name}</Link>
              </span>
              {task.asset_ref && (
                <span className="meta-item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                    <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
                  </svg>
                  <Link to={`/assets/${task.asset_id}`} className="asset-link">{task.asset_ref}</Link>
                  {extraRobotCount > 0 && (
                    <span className="asset-extra" title={allRobotRefs}> +{extraRobotCount}</span>
                  )}
                </span>
              )}
              <span className="meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                </svg>
                {crew.length > 0 ? engineerNameOf(crew[0]) : (task.assigned_to_name || unassignedLabel)}
                {extraEngineerCount > 0 && (
                  <span className="engineer-extra" title={allEngineerNames}> +{extraEngineerCount}</span>
                )}
              </span>
              {task.task_type === 'sales_action' && task.source_group_id && canOpenDeals && (
                <span className="meta-item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="20" x2="12" y2="10"/>
                    <line x1="18" y1="20" x2="18" y2="4"/>
                    <line x1="6" y1="20" x2="6" y2="16"/>
                  </svg>
                  <Link to={`/pipeline/groups/${task.source_group_id}`} className="asset-link" title="Open the opportunity this task came from">
                    {task.source_group_ref}{task.source_opp_ref ? ` · ${task.source_opp_ref}` : ''}
                  </Link>
                </span>
              )}
              {task.task_type === 'sales_action' && task.source_group_ref && !canOpenDeals && (
                <span className="meta-item" title="The opportunity this task came from">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="20" x2="12" y2="10"/>
                    <line x1="18" y1="20" x2="18" y2="4"/>
                    <line x1="6" y1="20" x2="6" y2="16"/>
                  </svg>
                  {task.source_group_ref}{task.source_opp_ref ? ` · ${task.source_opp_ref}` : ''}
                </span>
              )}
            </div>
            {/* On the Ops Planning board, or add it (Gareth 2026-10-21). */}
            <OpsBoardServiceStatus type="scheduled_task" id={id} />
          </div>
        </div>

        <div className="header-actions">
          {/* Edit opens the Schedule Engineer Work form on this job. Only for
              team leaders and up, hidden once the job is finished and on sales
              tasks (those are edited from the deal, not from here). */}
          {canSchedule && isOpen && task.task_type !== 'sales_action' && (
            <Link to={`/tasks/${id}/edit`} className="btn-action">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              Edit
            </Link>
          )}
          {offersReturnVisit && !everyNotDoneBooked && (
            <Link to={`/tasks/new?return_for=${encodeURIComponent(id)}`} className="btn-action primary">
              Book a return visit
            </Link>
          )}
          {offersReturnVisit && everyNotDoneBooked && coveringVisits.map(visit => (
            <Link key={visit.task_id} to={`/tasks/${visit.task_id}`} className="btn-action">
              Return visit {visit.task_ref}
            </Link>
          ))}
          {/* Someone logged the viewer on this job: they confirm or correct
              their own entry instead of filing the visit again. Shown on a
              finished job too - their hours still need signing off. */}
          {!readOnlyWorkspace && myOpenLog && (
            <button
              className="btn-action primary"
              onClick={() => setConfirmLog(myOpenLog)}
            >
              Confirm my hours
            </button>
          )}
          {canLogWork && (
            <button
              className="btn-action"
              onClick={() => setShowWorkLogModal(true)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              {continueLogWork ? 'Continue Log Work' : 'Log Work'}
            </button>
          )}
          {canLogWork && continueLogWork && continueNote && (
            <span className="log-work-continue-note">{continueNote}</span>
          )}
          {!readOnlyWorkspace && isOpen && mySignedOff && (
            <span className="hours-signed-off">Your hours on this job are signed off.</span>
          )}
          {!readOnlyWorkspace && ['scheduled', 'confirmed'].includes(task.status) && (
            <button className="btn-action primary" onClick={startTask}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="5 3 19 12 5 21 5 3"/>
              </svg>
              Start Task
            </button>
          )}
          {/* Completing a job goes through Log Work with a closing outcome —
              the old Mark Complete button closed jobs with no labour recorded. */}
          {canSchedule && isOpen && (
            <button className="btn-action danger" onClick={() => setShowCancelModal(true)}>
              Cancel
            </button>
          )}
          {/* The server refuses a job with work logged on it, or with Log
              Work part saved (it is cancelled instead): its sentence is shown
              under the header. */}
          {canDeleteRecords(user?.role) && (
            <DeleteRecordButton
              kind="job"
              reference={task.task_ref}
              endpoint={`/api/v1/tasks/${id}`}
              authFetch={authFetch}
              onDeleted={backToList}
              onRefused={setDeleteRefused}
            />
          )}
        </div>
      </div>

      {deleteRefused && (
        <div className="delete-refused" role="alert">
          <span>{deleteRefused}</span>
          <button type="button" className="btn-small" onClick={() => setDeleteRefused(null)}>Dismiss</button>
        </div>
      )}

      {payrollNotice && (
        <div className="modal-hint" role="status">
          {payrollNotice}
          <button type="button" className="btn-small" onClick={() => setPayrollNotice(null)}>Dismiss</button>
        </div>
      )}

      {namedByColleague && !readOnlyWorkspace && (
        <div className="modal-hint" role="status">
          {namedByColleague} is part-way through Log Work for this job and has named you.
        </div>
      )}

      {raisedFollowUps.length > 0 && (
        <div className="modal-hint follow-ups-raised" role="status">
          {raisedFollowUps.length === 1 ? 'Follow-up ticket raised: ' : 'Follow-up tickets raised: '}
          {raisedFollowUps.map((ticket, index) => (
            <React.Fragment key={ticket.ticket_id || index}>
              {index > 0 && ', '}
              {ticket.ticket_id
                ? <Link to={`/tickets/${ticket.ticket_id}`}>{ticket.ticket_ref || 'ticket'}</Link>
                : ticket.ticket_ref}
            </React.Fragment>
          ))}
          <button type="button" className="btn-small" onClick={() => setRaisedFollowUps([])}>Dismiss</button>
        </div>
      )}

      {/* Subject Card */}
      <div className="subject-card">
        <h2>{task.title || getTaskTypeLabel(task.task_type)}</h2>
        {task.description && <p className="description">{task.description}</p>}
      </div>

      {/* Tabs */}
      <div className="detail-tabs">
        <button
          className={activeTab === 'details' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('details')}
        >
          Details
        </button>
        <button
          className={activeTab === 'work' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('work')}
        >
          Work Logs ({task.work_logs?.length || 0})
        </button>
        <button
          className={activeTab === 'history' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('history')}
        >
          Assignment History
        </button>
      </div>

      {/* Tab Content */}
      <div className="detail-content">
        {activeTab === 'details' && (
          <div className="details-grid">
            {/* Schedule Card */}
            <div className="detail-card">
              <div className="card-header">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                <h3>SCHEDULE</h3>
              </div>
              <div className="card-body">
                <div className="info-row">
                  <span className="info-label">Date</span>
                  <span className="info-value">{formatScheduleDate(task.scheduled_date)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Time</span>
                  <span className="info-value">{formatScheduleTime(task.scheduled_start_time)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Est. Duration</span>
                  <span className="info-value">{formatDuration(task.estimated_duration_mins)}</span>
                </div>
                {task.actual_duration_mins && (
                  <div className="info-row">
                    <span className="info-label">Actual Duration</span>
                    <span className="info-value">{formatDuration(task.actual_duration_mins)}</span>
                  </div>
                )}
                {task.started_at && (
                  <div className="info-row">
                    <span className="info-label">Started</span>
                    <span className="info-value">{formatDateTime(task.started_at)}</span>
                  </div>
                )}
                {task.completed_at && (
                  <div className="info-row">
                    <span className="info-label">Completed</span>
                    <span className="info-value ok">{formatDateTime(task.completed_at)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Customer Card */}
            <div className="detail-card">
              <div className="card-header">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4z"/>
                </svg>
                <h3>CUSTOMER</h3>
              </div>
              <div className="card-body">
                <div className="info-row">
                  <span className="info-label">Customer</span>
                  <span className="info-value highlight">
                    <Link to={`/customers/${task.customer_id}`}>{task.customer_name}</Link>
                  </span>
                </div>
                {task.customer_number && (
                  <div className="info-row">
                    <span className="info-label">Customer No.</span>
                    <span className="info-value">{task.customer_number}</span>
                  </div>
                )}
                {task.distributor_name && (
                  <div className="info-row">
                    <span className="info-label">Distributor</span>
                    <span className="info-value">{task.distributor_name}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Asset Card */}
            <div className="detail-card">
              <div className="card-header">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                  <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
                </svg>
                <h3>ASSET</h3>
              </div>
              <div className="card-body">
                {/* One row per robot on the job (Phase 3), each with where it
                    stands on this visit: outstanding, done with its health,
                    or not done with the reason. */}
                {robots.length > 0 ? (
                  <div className="robot-list-block">
                    <span className="info-label">{robots.length > 1 ? 'Robots' : 'Asset'}</span>
                    <ul className="robot-list" aria-label={robots.length > 1 ? 'Robots on this job' : 'Asset on this job'}>
                      {robots.map(robot => (
                        <li key={robot.asset_id} className="robot-row">
                          <div className="robot-row-main">
                            <span className="info-value highlight">
                              <Link to={`/assets/${robot.asset_id}`}>{robot.asset_ref || robot.asset_id}</Link>
                            </span>
                            <span className="robot-row-meta">
                              {robot.serial_number || '-'} · {robot.robot_type ? String(robot.robot_type).replace(/_/g, ' ') : '-'}
                            </span>
                          </div>
                          <div className="robot-row-state">
                            <RobotStateChip robot={robot} jobStatus={task.status} />
                            {robot.visit_outcome === 'not_done' && robot.outcome_note && (
                              <span className="robot-row-note">{robot.outcome_note}</span>
                            )}
                            {/* Phase 6 step 4: the follow-up ticket raised for
                                this robot on this job, with where it stands. */}
                            {robot.follow_up_ticket_id && (
                              <span className="robot-row-note robot-row-follow-up">
                                Follow-up{' '}
                                <Link to={`/tickets/${robot.follow_up_ticket_id}`}>{robot.follow_up_ticket_ref || 'ticket'}</Link>
                                {robot.follow_up_ticket_status ? ` (${ticketStatusLabel(robot.follow_up_ticket_status)})` : ''}
                              </span>
                            )}
                            {/* Phase 6 step 5: the return visit a not-done
                                robot is booked on. */}
                            {bookedVisitOf[robot.asset_id] && (
                              <span className="robot-row-note robot-row-booked">
                                <Link to={`/tasks/${bookedVisitOf[robot.asset_id].task_id}`}>
                                  {bookedOnSentence(bookedVisitOf[robot.asset_id])}
                                </Link>
                              </span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="no-data">No asset linked</p>
                )}
              </div>
            </div>

            {/* Assignment Card */}
            <div className="detail-card">
              <div className="card-header">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                </svg>
                <h3>ASSIGNMENT</h3>
              </div>
              <div className="card-body">
                {/* Phase 5: one row per engineer booked on the job, the lead
                    first and marked, each with where their hours stand. A
                    group job still names the group. */}
                {crew.length > 0 ? (
                  <div className="crew-list-block">
                    <span className="info-label">{crew.length > 1 ? 'Engineers' : 'Assigned To'}</span>
                    <ul className="crew-list" aria-label="Engineers on this job">
                      {crew.map((engineer, index) => {
                        const hours = crewHoursStatus(engineer.user_id, workLogs, unfinishedIds);
                        return (
                          <li key={engineer.user_id} className="crew-row">
                            <div className="crew-row-main">
                              <span className="info-value">{engineerNameOf(engineer)}</span>
                              {index === 0 && crew.length > 1 && (
                                <span className="crew-lead-badge">Lead</span>
                              )}
                            </div>
                            <span className={`crew-hours crew-hours-${hours.key}`}>{hours.label}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : (
                  <div className="info-row">
                    <span className="info-label">Assigned To</span>
                    <span className="info-value">{task.assigned_to_name || unassignedLabel}</span>
                  </div>
                )}
                <div className="info-row">
                  <span className="info-label">Created By</span>
                  <span className="info-value">{task.created_by_name || '-'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Created</span>
                  <span className="info-value">{formatDateTime(task.created_at)}</span>
                </div>
                {task.linked_ticket_ref && (
                  <div className="info-row">
                    <span className="info-label">Linked Ticket</span>
                    <span className="info-value highlight">
                      <Link to={`/tickets/${task.linked_ticket_id}`}>{task.linked_ticket_ref}</Link>
                    </span>
                  </div>
                )}
                {/* Phase 6 step 5: the finished job this one goes back to
                    finish, and the return visits booked from this one. */}
                {task.return_visit_of_task_id && (
                  <div className="info-row">
                    <span className="info-label">Return visit for</span>
                    <span className="info-value highlight">
                      <Link to={`/tasks/${task.return_visit_of_task_id}`}>{task.return_visit_of_task_ref || 'the earlier job'}</Link>
                    </span>
                  </div>
                )}
                {returnVisits.length > 0 && (
                  <div className="info-row">
                    <span className="info-label">Return visits</span>
                    <span className="info-value highlight">
                      {returnVisits.map((visit, index) => {
                        const day = shortDayLabel(visit.scheduled_date);
                        return (
                          <React.Fragment key={visit.task_id}>
                            {index > 0 && ', '}
                            <Link to={`/tasks/${visit.task_id}`}>{visit.task_ref || 'return visit'}</Link>
                            {day ? ` (${day})` : ''}
                          </React.Fragment>
                        );
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Outcome Card (once outcome data exists) */}
            {(task.outcome_notes || task.health_score_recorded) && (
              <div className="detail-card full-width">
                <div className="card-header">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <h3>OUTCOME</h3>
                </div>
                <div className="card-body">
                  {task.health_score_recorded && (
                    <div className="info-row">
                      <span className="info-label">Health Score</span>
                      <span className={`info-value health-badge health-${task.health_score_recorded}`}>
                        {task.health_score_recorded}
                      </span>
                    </div>
                  )}
                  {task.outcome_notes && (
                    <div className="info-row">
                      <span className="info-label">Outcome Notes</span>
                      <span className="info-value">{task.outcome_notes}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Attachments Card */}
            {Array.isArray(task.attachments) && task.attachments.length > 0 && (
              <div className="detail-card full-width">
                <div className="card-header">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
                  </svg>
                  <h3>ATTACHMENTS</h3>
                </div>
                <div className="card-body">
                  <AttachmentList attachments={task.attachments} />
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'work' && (
          <div className="work-logs-section">
            {reopenMessage && (
              <div className="modal-hint" role="status">
                {reopenMessage}
                <button type="button" className="btn-small" onClick={() => setReopenMessage(null)}>Dismiss</button>
              </div>
            )}
            <div className="list-header">
              <h3>Work Logs</h3>
              {canLogWork && (
                <button className="btn-small" onClick={() => setShowWorkLogModal(true)}>
                  {continueLogWork ? '+ Continue Log Work' : '+ Log Work'}
                </button>
              )}
            </div>
            {canLogWork && continueLogWork && continueNote && (
              <div className="log-work-continue-note">{continueNote}</div>
            )}
            {/* Saved Log Work (spec 6.4): not hours until Complete Work Log. */}
            <SavedLogWorkPanel
              saved={savedLogWork}
              assetRefs={assetRefs}
              canContinue={canLogWork}
              canClear={canClearHere}
              onContinue={() => setShowWorkLogModal(true)}
              onClear={() => setShowClearModal(true)}
            />
            {!task.work_logs || task.work_logs.length === 0 ? (
              <div className="empty-message">No work logged yet</div>
            ) : (
              <div className="work-logs-list">
                {groupCrewLogs(task.work_logs).map(log => {
                  // Parts are filed on the crew's lead log, so they show under the lead's card.
                  const logParts = partsSummaryForLog(parts, log.log_id);
                  const reopenOffers = reopenFor(log);
                  return (
                    <div key={log.log_id} className={`work-log-item${isPeerLog(log) ? ' crew-peer-log' : ''}`}>
                      <div className="log-header">
                        <span className="log-type">{String(log.log_type).replace(/_/g, ' ')}</span>
                        {log.reopen?.status === 'requested' && <span className="status-badge reopen-requested">Reopen requested</span>}
                        {log.reopen?.status === 'open' && <span className="status-badge reopened">Reopened</span>}
                        <span className="log-date">{formatDateTime(log.started_at)}</span>
                        {reopenOffers.update && (
                          <button
                            type="button"
                            className="btn-log-action"
                            onClick={() => setReopenWindow({ kind: 'update', log })}
                          >
                            Update work log
                          </button>
                        )}
                        {reopenOffers.request && (
                          <button
                            type="button"
                            className="btn-log-action"
                            onClick={() => setReopenWindow({ kind: 'request', log })}
                          >
                            Request reopen
                          </button>
                        )}
                        {reopenOffers.cancel && (
                          <button
                            type="button"
                            className="btn-log-action"
                            onClick={() => cancelReopen(log)}
                          >
                            Cancel request
                          </button>
                        )}
                        {reopenOffers.decide && (
                          <>
                            <button
                              type="button"
                              className="btn-log-action"
                              onClick={() => setReopenWindow({ kind: 'approve', log })}
                            >
                              Reopen
                            </button>
                            <button
                              type="button"
                              className="btn-log-action"
                              onClick={() => setReopenWindow({ kind: 'decline', log })}
                            >
                              Decline
                            </button>
                          </>
                        )}
                        {reopenOffers.reopen && (
                          <button
                            type="button"
                            className="btn-log-action"
                            onClick={() => setReopenWindow({ kind: 'reopen', log })}
                          >
                            Reopen
                          </button>
                        )}
                        {canConfirmLog(log) && (
                          <button
                            type="button"
                            className="btn-log-action"
                            onClick={() => setConfirmLog(log)}
                          >
                            Confirm my hours
                          </button>
                        )}
                        {canChangeCrew(log) && (
                          <button
                            type="button"
                            className="btn-log-action"
                            onClick={() => setCrewModalLog(log)}
                          >
                            Change crew
                          </button>
                        )}
                      </div>
                      <div className="log-body">
                        {/* Who else was on this visit: a crew's logs are linked
                            and shown together, each person's own entry with
                            whether they have signed it off. */}
                        {crewFor(log).length > 1 && (
                          <div className="log-crew">
                            <span className="log-crew-label">On this job:</span>
                            {crewFor(log).map(member => (
                              <span key={member.log_id} className="log-crew-member">
                                {member.engineer_name || 'Unknown engineer'}
                                {member.log_id === log.work_log_group_id ? ' (led)' : ''}
                                {member.outcome ? '' : ' - not signed off'}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="log-meta">
                          <span>By {log.engineer_name}</span>
                          {log.duration_minutes && <span>Duration: {formatDuration(log.duration_minutes)}</span>}
                          <span className={`period-badge ${log.work_period}`}>{String(log.work_period).replace(/_/g, ' ')}</span>
                        </div>
                        {log.notes && <p className="log-notes">{log.notes}</p>}
                        {log.outcome && (
                          <div className="log-outcome">Outcome: {String(log.outcome).replace(/_/g, ' ')}</div>
                        )}
                        {logParts && <div className="log-notes log-parts">Parts used on this log: {logParts}</div>}
                        {/* The forms filled in on this log, each opening read only. */}
                        <WorkLogFormList
                          forms={logForms.filter(form => form.work_log_id === log.log_id)}
                          onOpen={form => setOpenFormId(form.check_id)}
                        />
                        <AttachmentList attachments={log.attachments} compact />
                        <ReopenStatusLine reopen={log.reopen} />
                        {log.reopen && (
                          <WorkLogChanges basePath={logPath(log)} authFetch={authFetch} refreshKey={changesKey} />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Parts used (spare parts, spec 8.5): what was recorded on this
                job, and (6.5) any Parts used this phone could not record
                because the job was closed first. */}
            {recordedAfterAll.length > 0 && (
              <div className="modal-hint" role="status">
                {`${listJoin(recordedAfterAll)} ${recordedAfterAll.length === 1 ? 'was' : 'were'} recorded after all.`}
                <button type="button" className="btn-small" onClick={() => setRecordedAfterAll([])}>Dismiss</button>
              </div>
            )}
            {/* Only against the job's Parts used as just read: on a list that
                failed to load, a line recorded after all would be listed as
                missing, and a team leader would add it twice. */}
            {partsReady && staleSpareCopies.filter(copy => copy.missing.length > 0).map(copy => (
              <div key={`stale-spares:${copy.id}`} className="modal-hint warning saved-log-work-choice stale-spare-parts" role="alert">
                <span>
                  {`Parts used on this phone were not recorded because the job was closed first: ${listJoin(copy.missing.map(line => `${spareLineLabel(line.name, line.itemNumber)} ×${quantityText(line.quantity)}`))}. Ask a team leader to add ${copy.missing.length === 1 ? 'it' : 'them'} on this page.`}
                </span>
                <button type="button" className="btn-small secondary" onClick={() => discardStaleSpares(copy)}>Discard</button>
              </div>
            ))}
            {partsStatus === 'failed' && staleSpareCopies.length > 0 && (
              <div className="modal-hint warning stale-spare-parts" role="status">
                The job&apos;s Parts used could not be read, so the parts on this phone cannot be checked yet.
              </div>
            )}
            {(task.task_type !== 'sales_action' || parts.length > 0) && (
              <PartsUsedList
                rows={parts}
                robots={robots}
                showRobot
                allowAdd={canCorrectParts(user?.role) && task.task_type !== 'sales_action'}
                taskId={id}
                catalogue={spareParts}
                currentUser={user}
                authFetch={authFetch}
                onChanged={fetchParts}
                status={partsStatus}
                onRetry={fetchParts}
                subject="job"
              />
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="review-notes-section">
            {/* Notes from the Jobs & Tickets board review (2026-10-20). */}
            <ReviewNotes type="scheduled_task" id={id} />
          </div>
        )}

        {activeTab === 'history' && (
          <div className="assignment-section">
            <div className="list-header">
              <h3>Assignment History</h3>
            </div>
            {!task.assignment_history || task.assignment_history.length === 0 ? (
              <div className="empty-message">No assignment history</div>
            ) : (
              <div className="assignment-list">
                {task.assignment_history.map(entry => (
                  <div key={entry.assignment_id} className="assignment-item">
                    <div className="assignment-header">
                      <span className="assignment-date">{formatDateTime(entry.assigned_at)}</span>
                    </div>
                    <div className="assignment-body">
                      <span>
                        {entry.assigned_from_name || entry.assigned_from_group ? `${entry.assigned_from_name || 'Maintenance Planning Owner group'} → ` : ''}
                        <strong>{entry.assigned_to_name || 'Maintenance Planning Owner group'}</strong>
                      </span>
                      <span className="assigned-by">by {entry.assigned_by_name}</span>
                    </div>
                    {entry.handover_notes && (
                      <p className="handover-notes">{entry.handover_notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {openFormId && (
        <WorkLogFormViewer checkId={openFormId} authFetch={authFetch} onClose={() => setOpenFormId(null)} linkToWork={false} />
      )}

      {/* Work Log Modal */}
      {showWorkLogModal && (
        <TaskWorkLogModal
          taskId={id}
          task={task}
          authFetch={authFetch}
          config={workLogConfig}
          engineers={engineers}
          currentUser={user}
          savedLogWork={savedLogWork}
          spareParts={spareParts}
          onClose={() => setShowWorkLogModal(false)}
          onSavedForLater={(message) => {
            setShowWorkLogModal(false);
            if (message) setPayrollNotice(message);
          }}
          onSaved={(notice, followUps) => {
            setShowWorkLogModal(false);
            fetchTask();
            fetchParts();
            setPayrollNotice(notice || null);
            setRaisedFollowUps(Array.isArray(followUps) ? followUps : []);
          }}
        />
      )}

      {/* Confirm my hours: the co-engineer's own entry (Phase 4) */}
      {confirmLog && (
        <ConfirmHoursModal
          taskId={id}
          log={confirmLog}
          leadName={leadNameFor(confirmLog)}
          authFetch={authFetch}
          journeys={[...(savedLive ? savedLogWork.myUnfiledJourneys : []), ...myJourneys]}
          onClose={() => setConfirmLog(null)}
          onSaved={(notice) => {
            setConfirmLog(null);
            fetchTask();
            setPayrollNotice(notice || null);
          }}
        />
      )}

      {reopenWindow?.kind === 'request' && (
        <RequestReopenModal
          basePath={logPath(reopenWindow.log)}
          authFetch={authFetch}
          onClose={() => setReopenWindow(null)}
          onDone={(reopen) => reopenStepDone(
            `Your request has been sent${reopen?.sent_to ? ` to ${reopen.sent_to}` : ''}. You will be told when the work log is reopened.`
          )}
        />
      )}

      {(reopenWindow?.kind === 'approve' || reopenWindow?.kind === 'decline') && (
        <DecideReopenModal
          basePath={logPath(reopenWindow.log)}
          log={reopenWindow.log}
          reopen={reopenWindow.log.reopen}
          mode={reopenWindow.kind}
          authFetch={authFetch}
          onClose={() => setReopenWindow(null)}
          onDone={() => reopenStepDone(reopenWindow.kind === 'approve'
            ? `The work log is reopened. ${reopenWindow.log.engineer_name || 'The engineer'} has been told.`
            : `The request was declined. ${reopenWindow.log.engineer_name || 'The engineer'} has been told why.`)}
        />
      )}

      {reopenWindow?.kind === 'reopen' && (
        <ReopenLogModal
          basePath={logPath(reopenWindow.log)}
          log={reopenWindow.log}
          authFetch={authFetch}
          onClose={() => setReopenWindow(null)}
          onDone={() => reopenStepDone(
            `The work log is reopened. ${reopenWindow.log.engineer_name || 'The engineer'} has been told.`
          )}
        />
      )}

      {reopenWindow?.kind === 'update' && (
        <UpdateWorkLogModal
          basePath={logPath(reopenWindow.log)}
          kind="job"
          taskId={id}
          robots={robots.map(robot => ({ asset_id: lowerId(robot.asset_id), asset_ref: robot.asset_ref }))}
          log={reopenWindow.log}
          user={user}
          closedTheWork={completedTheJob(reopenWindow.log)}
          summary={{ completion_summary: task.outcome_notes }}
          forms={logForms.filter(form => form.work_log_id === reopenWindow.log.log_id)}
          catalogue={user && canFitParts(user.role) && task.task_type !== 'sales_action' ? spareParts : null}
          recordedParts={partsSummaryForLog(parts, reopenWindow.log.log_id) || ''}
          authFetch={authFetch}
          onClose={() => setReopenWindow(null)}
          onSaved={(result) => {
            if (result.payrollNotice) setPayrollNotice(result.payrollNotice);
            const notAgain = result.alreadyRecorded.length > 0
              ? ` Already recorded, so not added again: ${result.alreadyRecorded.join(', ')}.`
              : '';
            reopenStepDone(`${result.completed ? COMPLETED_AGAIN_MESSAGE : SAVED_STILL_REOPENED_MESSAGE}${notAgain}`);
          }}
        />
      )}

      {/* Change crew: a team leader or manager correcting who was on the job */}
      {crewModalLog && (
        <JobCrewModal
          taskId={id}
          leadLog={crewModalLog}
          crew={crewFor(crewModalLog)}
          engineers={engineers}
          authFetch={authFetch}
          presetAdd={crewModalPreset ? crewModalPreset.add : []}
          presetReason={crewModalPreset ? crewModalPreset.reason : ''}
          presetNote={crewModalPreset ? crewModalPreset.note : null}
          onClose={() => { setCrewModalLog(null); setCrewModalPreset(null); }}
          onSaved={() => {
            setCrewModalLog(null);
            setCrewModalPreset(null);
            fetchTask();
            savedLogWork.refresh();
          }}
        />
      )}

      {/* Clear unfinished Log Work: team leaders and up, with a reason */}
      {showClearModal && (
        <ClearLogWorkModal
          taskId={id}
          task={task}
          saved={savedLogWork}
          config={workLogConfig}
          authFetch={authFetch}
          onClose={() => setShowClearModal(false)}
          onCleared={() => {
            setShowClearModal(false);
            savedLogWork.refresh();
            fetchTask();
          }}
          onAddToCrew={(leadLog, journey, reason) => {
            const logOnPage = workLogs.find(log => log.log_id === leadLog.log_id)
              || { ...leadLog, work_log_group_id: null };
            const name = journey.full_name || 'This engineer';
            const first = firstNameOf(name);
            setShowClearModal(false);
            setCrewModalPreset({
              add: journey.engineer_id ? [journey.engineer_id] : [],
              reason,
              note: `${first}'s entry takes that log's times and work period; ${first} corrects the times with Confirm my hours.`
            });
            setCrewModalLog(logOnPage);
          }}
        />
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Cancel Task</h2>
              <button className="modal-close" onClick={() => { setShowCancelModal(false); setCancelError(null); }}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              {cancelError && <div className="modal-error" role="alert">{cancelError}</div>}
              {cancelBlockedBy && (
                <div className="modal-hint warning" role="note">
                  {cancelBlockedBy} has unfinished Log Work on this job. Cancelling is refused until it is saved or cleared.
                </div>
              )}
              <div className="form-group">
                <label>Reason for cancellation</label>
                <textarea
                  value={cancelReason}
                  onChange={e => setCancelReason(e.target.value)}
                  rows={3}
                  placeholder="Why is this task being cancelled?"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => { setShowCancelModal(false); setCancelError(null); }}>
                Keep Task
              </button>
              <button className="btn-danger" onClick={cancelTask}>
                Cancel Task
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmElement}
    </div>
  );
}

function AttachmentList({ attachments = [], compact = false }) {
  const rows = Array.isArray(attachments) ? attachments : [];
  if (rows.length === 0) return null;

  return (
    <div className={compact ? 'attachment-list compact' : 'attachment-list'}>
      {rows.map((attachment, index) => {
        const href = authedFileHref(attachment.url);

        return attachment.url ? (
          <a key={attachment.attachment_id || index} href={href} target="_blank" rel="noreferrer" className="attachment-chip">
            {attachment.name || `Attachment ${index + 1}`}
          </a>
        ) : (
          <span key={attachment.attachment_id || index} className="attachment-chip muted">
            {attachment.name || `Attachment ${index + 1}`}
          </span>
        );
      })}
    </div>
  );
}

/**
 * Change who was on a job after it was logged (Phase 4). A team leader or
 * manager, with a written reason, and allowed on a closed lead log: a job's
 * lead log is born closed, so the ticket rule of "while the lead's log is
 * open" never applies.
 * Adding someone creates their own open work log with the lead's times;
 * removing someone deletes theirs, only while they have not signed it off.
 * The server enforces all of it, audits it and tells each engineer.
 *
 * The twin of TicketDetail's CrewChangeModal, posting to the job's route.
 */
function JobCrewModal({
  taskId, leadLog, crew, engineers, authFetch, onClose, onSaved,
  // Saved Log Work (spec 6.4): opened from Clear unfinished Log Work, with the
  // engineer whose Job Stages are in no hours ticked and the reason filled in.
  presetAdd = [], presetReason = '', presetNote = null
}) {
  const currentIds = crew.map(member => member.engineer_id).filter(engineerId => engineerId !== leadLog.engineer_id);
  const [selected, setSelected] = useState(() => [
    ...currentIds,
    ...(Array.isArray(presetAdd) ? presetAdd : [])
      .filter(engineerId => engineerId && engineerId !== leadLog.engineer_id && !currentIds.includes(engineerId))
  ]);
  const [reason, setReason] = useState(presetReason || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const options = (engineers || []).filter(engineer => engineer.user_id && engineer.user_id !== leadLog.engineer_id);
  // Someone on the crew who has since left the engineers list still shows by name.
  const crewNames = Object.fromEntries(
    crew.map(member => [member.engineer_id, member.engineer_name]).filter(([, name]) => name)
  );

  const add = selected.filter(engineerId => !currentIds.includes(engineerId));
  const remove = currentIds.filter(engineerId => !selected.includes(engineerId));
  const changed = add.length > 0 || remove.length > 0;
  // Mirrors the server's minimum so the button does not promise a save that is refused.
  const reasonOk = reason.trim().length >= 5;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await authFetch(`/api/v1/tasks/${taskId}/work-log/${leadLog.log_id}/crew`, {
        method: 'POST',
        body: JSON.stringify({ add, remove, reason: reason.trim() })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'The crew could not be changed. Try again.');
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const leadName = leadLog.engineer_name || "The log's author";

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={event => event.stopPropagation()} style={{ maxWidth: '520px' }}>
        <div className="modal-header">
          <h2>Who was on this job</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="modal-error" role="alert">{error}</div>}
          <div className="form-group">
            <label htmlFor="job_crew_engineer_picker">Engineers</label>
            <EngineerPicker
              id="job_crew_engineer_picker"
              options={options}
              selectedIds={selected}
              onChange={setSelected}
              knownNames={crewNames}
              emptyText={`Only ${leadLog.engineer_name || "the log's author"} is on this job.`}
            />
            <span className="field-help">
              {leadName} logged this job and cannot be removed.
              Adding someone creates a work log for them with the same times, for them to check and sign off.
              Removing someone deletes theirs, which is only possible while they have not signed it off.
            </span>
            {presetNote && <span className="field-help">{presetNote}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="job_crew_reason">Why is this changing? *</label>
            <textarea
              id="job_crew_reason"
              rows={2}
              value={reason}
              onChange={event => setReason(event.target.value)}
              placeholder="e.g. Mike was on this job too but was not added"
            />
            <span className="field-help">
              Recorded against the work log, and sent to each engineer whose hours change.
            </span>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn-save"
            onClick={submit}
            disabled={saving || !changed || !reasonOk}
          >
            {saving ? 'Saving...' : 'Save crew'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The Log Work window
// ---------------------------------------------------------------------------

/** A robot's sign-off as its saved part holds it. */
const robotOutcomeFromPart = (value) => ({
  ...emptyRobotOutcome(),
  outcome: value && value.outcome === 'not_done' ? 'not_done' : 'done',
  note: (value && value.note) || '',
  health_score: (value && value.health_score) || '',
  follow_up: { ...emptyRobotFollowUp(), ...((value && value.follow_up) || {}) }
});

/** Photos of one saved part as a van check keeps them: sent ones, then those still on the phone. */
function photosOfPart(saved, partKey) {
  const files = Array.isArray(saved && saved.files) ? saved.files : [];
  const photos = Array.isArray(saved && saved.photos) ? saved.photos : [];
  return [
    ...files.filter(file => file.part_key === partKey).map(file => attachmentFromFileRef(file)),
    ...photos.filter(photo => photo.part_key === partKey && photo.state !== 'failed')
      .map(photo => attachmentFromFileRef({ client_ref: photo.client_ref, name: photo.name, size: photo.size, type: photo.type }))
  ];
}

/**
 * A photo list as it is filed: the screen's photos, linked to their sent
 * files, plus any sent file of that part the screen does not hold (added on
 * another phone and not yet shown). The server files only what the body
 * carries.
 */
function withSavedFiles(list, files, sentOfPart) {
  const linked = linkSentAttachments(Array.isArray(list) ? list : [], files) || [];
  const held = new Set();
  linked.forEach(item => {
    if (item && item.file_id) held.add(item.file_id);
    if (item && item.client_ref) held.add(item.client_ref);
  });
  const missing = (sentOfPart || []).filter(file => file
    && !(file.file_id && held.has(file.file_id)) && !(file.client_ref && held.has(file.client_ref)));
  return [...linked, ...missing];
}

/**
 * Photos still on the phone, read as their content ({ name, size, type,
 * dataUrl }) by client_ref, for a save made after the store went away.
 */
async function queuedPhotoContent(saved) {
  const content = new Map();
  const queued = (Array.isArray(saved && saved.photos) ? saved.photos : [])
    .filter(photo => photo && photo.state !== 'failed' && photo.blob);
  for (const photo of queued) {
    const file = typeof File !== 'undefined' && !(photo.blob instanceof File)
      ? new File([photo.blob], photo.name || 'photo', { type: photo.type || photo.blob.type || '' })
      : photo.blob;
    // eslint-disable-next-line no-await-in-loop
    const [payload] = await filesToAttachmentPayload([file]);
    content.set(photo.client_ref, {
      name: photo.name || payload.name,
      size: photo.size || payload.size,
      type: photo.type || payload.type,
      dataUrl: payload.dataUrl
    });
  }
  return content;
}

/** A second van part as its check: the check fields only. */
const secondVanCheckOf = (value) => {
  const { driver_id: driverId, check_open: checkOpen, ...check } = value || {};
  return check;
};

/**
 * formData with the work, notes and outcome parts put back (restore step 1).
 * "Back to me" chosen by someone else is shown unset.
 */
function formFromParts(base, { work = null, notes = null, outcome = null }, meKey) {
  const next = { ...base };
  if (work) {
    if (work.log_type) next.log_type = work.log_type;
    if (work.work_period) next.work_period = work.work_period;
    if (work.started_at) next.started_at = toLocalDateTimeInput(work.started_at);
    if (work.ended_at) next.ended_at = toLocalDateTimeInput(work.ended_at);
  }
  if (notes) next.notes = typeof notes.notes === 'string' ? notes.notes : '';
  if (outcome) {
    const takenByOther = outcome.assign_next_type === 'self' && outcome.self_user_id
      && lowerId(outcome.self_user_id) !== meKey;
    next.outcome = outcome.outcome || '';
    next.completion_summary = outcome.completion_summary || '';
    next.assign_next_type = takenByOther ? '' : (outcome.assign_next_type || '');
    next.assigned_to_next_id = outcome.assigned_to_next_id || '';
    next.follow_up_ticket_required = Boolean(outcome.follow_up_ticket_required);
    next.follow_up_subject = outcome.follow_up_subject || '';
    next.follow_up_description = outcome.follow_up_description || '';
    next.follow_up_priority = outcome.follow_up_priority || '';
    if (outcome.follow_up_asset_id) next.follow_up_asset_id = outcome.follow_up_asset_id;
  }
  return next;
}

/** What the window starts from when it opens on a saved Log Work (spec 6.4 restore order). */
function restoreFromSaved(saved, meKey) {
  const parts = (saved && saved.parts) || {};
  const valueOf = (key) => (parts[key] && parts[key].value) || null;
  const journeyValue = valueOf(`journey:${meKey}`);
  const journeyId = journeyValue && journeyValue.travel_id ? journeyValue.travel_id : null;
  const journeyRow = journeyId
    ? (Array.isArray(saved.journeys) ? saved.journeys : []).find(row => lowerId(row.travel_id) === lowerId(journeyId))
    : null;
  return {
    parts,
    work: valueOf('work'),
    notes: valueOf('notes'),
    outcome: valueOf('outcome'),
    outcomeBy: parts.outcome ? parts.outcome.changed_by_name || null : null,
    crew: valueOf('crew'),
    van: valueOf('van_check'),
    second: valueOf('second_van'),
    vanPhotos: photosOfPart(saved, 'van_check'),
    secondPhotos: photosOfPart(saved, 'second_van'),
    travelRecord: journeyId ? {
      travel_id: journeyId,
      departed_at: journeyRow ? journeyRow.departed_at : null,
      arrived_at: journeyRow ? journeyRow.arrived_at : null,
      departed_site_at: journeyRow ? journeyRow.departed_site_at : null,
      returned_at: journeyRow ? journeyRow.returned_at : null
    } : null
  };
}

const isoOf = (localValue) => {
  if (!localValue) return null;
  const date = new Date(localValue);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

// Which saved part each field of the window's form belongs to.
const FORM_FIELD_PART = {
  log_type: 'work',
  work_period: 'work',
  started_at: 'work',
  ended_at: 'work',
  notes: 'notes',
  outcome: 'outcome',
  completion_summary: 'outcome',
  assign_next_type: 'outcome',
  assigned_to_next_id: 'outcome',
  follow_up_ticket_required: 'outcome',
  follow_up_subject: 'outcome',
  follow_up_description: 'outcome',
  follow_up_priority: 'outcome',
  follow_up_asset_id: 'outcome'
};

const PHOTO_STATE_WORDS = { sent: 'Sent', waiting: 'Waiting to send', sending: 'Sending' };

// Reading the restored Job Stages record again after no signal.
const JOURNEY_RETRY_MS = [5000, 15000, 60000];

export const LOST_REPLY_MESSAGE = 'No signal - we could not tell if Log Work was saved. Tap Complete Work Log again; it will not be filed twice.';
export const CARRY_ON_UNAVAILABLE_MESSAGE = 'Carry on tomorrow cannot be saved just now. Choose who takes the job next, then save again.';

/**
 * The window's saved Log Work was saved or cleared (by anyone, anywhere):
 * nothing more can be filed from it. null while it is still the active one.
 *
 * @returns {null | { draftId, savedByMe, message }}
 */
function closedDraftOf(saved, draftId, meKey) {
  if (!saved || !draftId || saved.status !== 'ready') return null;
  if (lowerId(saved.draftId) === lowerId(draftId)) return null;
  const last = saved.lastSaved;
  if (last && lowerId(last.draft_id) === lowerId(draftId)) {
    if (last.saved_at && (!last.cleared_at || new Date(last.saved_at) >= new Date(last.cleared_at))) {
      const mine = Boolean(meKey) && lowerId(last.saved_by) === meKey;
      const who = mine ? 'You' : (last.saved_by_name || 'Someone');
      return {
        draftId,
        savedByMe: mine,
        savedAt: last.saved_at,
        message: `${who} saved this Log Work as hours ${logWorkAtWhen(last.saved_at)}, so nothing more can be saved in it. Close Log Work and open it again to start the next one.`
      };
    }
    if (last.cleared_at) {
      const reason = String(last.cleared_reason || '').trim();
      return {
        draftId,
        savedByMe: false,
        message: `${last.cleared_by_name || 'Someone'} cleared this Log Work ${logWorkAtWhen(last.cleared_at)}${reason ? `: ${reason.replace(/[.\s]+$/, '')}` : ''}. Nothing in it was saved as hours. Close Log Work and open it again to start a new one.`
      };
    }
  }
  return {
    draftId,
    savedByMe: false,
    message: 'This Log Work was saved or cleared on another phone, so nothing more can be saved in it. Close Log Work and open it again.'
  };
}

/**
 * The Log Work window. With the saved Log Work live (spec 6.4) it opens on
 * what the crew has saved so far and saves each change as it is made; while
 * the saved Log Work is still loading it says so; when it is unavailable
 * (before release, or a 404/503) it is exactly the window it always was.
 */
function TaskWorkLogModal(props) {
  const status = props.savedLogWork ? props.savedLogWork.status : 'unavailable';
  if (status === 'loading') {
    return (
      <div className="modal-overlay">
        <div className="modal-content work-log-modal" onClick={event => event.stopPropagation()}>
          <div className="modal-header">
            <h2>Log Work</h2>
            <button className="modal-close" onClick={props.onClose}>&times;</button>
          </div>
          <div className="modal-body">
            <div className="modal-hint" role="status">Opening your saved Log Work...</div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={props.onClose}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }
  return <TaskWorkLogForm {...props} liveAtOpen={status === 'ready' || status === 'offline'} />;
}

function TaskWorkLogForm({
  taskId, task, authFetch, config, engineers, currentUser, onClose, onSaved,
  savedLogWork = null, onSavedForLater = null, liveAtOpen = false,
  // Spare parts (spec 6.4): the page's useSparePartsList(), for the picker.
  spareParts = null
}) {
  // Saved Log Work: decided once, when the window opens, so the window never
  // changes how it behaves under the engineer's hands.
  const [live] = useState(() => Boolean(liveAtOpen && savedLogWork));
  const saved = live ? savedLogWork : null;
  // The store can answer "unavailable" after the window opened on it (it
  // opened with no signal, then a 404 or 503): from then on photos, Carry on
  // and the save work as they did before saved Log Work, read at the point of
  // use rather than fixed when the window opened.
  const storeUp = live && Boolean(savedLogWork) && savedLogWork.status !== 'unavailable';
  const savedRef = useRef(savedLogWork);
  savedRef.current = savedLogWork;
  const currentUserId = currentUser?.userId || null;
  const meKey = lowerId(currentUserId);
  const journeyKey = `journey:${meKey}`;
  const [restore] = useState(() => (live ? restoreFromSaved(savedLogWork, meKey) : null));
  const { confirmElement, requestConfirm } = useConfirm();

  // Some steps need the screen as the hook last left it: this resolves after
  // the next render, when savedRef holds the latest view.
  const [, forceRender] = useState(0);
  const renderWaitersRef = useRef([]);
  useEffect(() => {
    if (renderWaitersRef.current.length === 0) return;
    const waiters = renderWaitersRef.current;
    renderWaitersRef.current = [];
    waiters.forEach(resolve => resolve());
  });
  const afterRender = () => new Promise(resolve => {
    renderWaitersRef.current.push(resolve);
    forceRender(count => count + 1);
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  // Typed times are saved only once the engineer typed or restored them: the
  // "now" a new window starts with is a default, not something they chose.
  const touchedTimesRef = useRef({
    started_at: Boolean(restore && restore.work && restore.work.started_at),
    ended_at: Boolean(restore && restore.work && restore.work.ended_at)
  });
  const [selfTakenBy] = useState(() => {
    const outcome = restore && restore.outcome;
    return outcome && outcome.assign_next_type === 'self' && outcome.self_user_id && lowerId(outcome.self_user_id) !== meKey
      ? (restore.outcomeBy || 'A colleague')
      : null;
  });
  const [formData, setFormData] = useState(() => {
    const base = {
      log_type: config.work_types?.[0]?.value || 'site_work',
      started_at: toLocalDateTimeInput(),
      ended_at: '',
      work_period: config.work_periods?.[0]?.value || 'in_hours',
      notes: '',
      outcome: '',
      attachments: [],
      assign_next_type: '',
      assigned_to_next_id: '',
      completion_summary: '',
      follow_up_ticket_required: false,
      follow_up_subject: '',
      follow_up_description: '',
      follow_up_priority: '',
      // Which robot a follow-up ticket is about: the primary robot unless the
      // engineer picks another (only asked with several robots, Phase 3).
      follow_up_asset_id: (task?.task_type !== 'sales_action' && robotsOnJob(task)[0]?.asset_id) || ''
    };
    return restore ? formFromParts(base, restore, meKey) : base;
  });
  const formRef = useRef(formData);
  formRef.current = formData;

  /** Saves one part of the saved Log Work (a no-op before release). */
  const persist = (key, value, options) => {
    if (!live || !savedRef.current) return Promise.resolve({ ok: false, unavailable: true });
    return savedRef.current.savePart(key, value, options);
  };
  const workPartOf = (form) => ({
    log_type: form.log_type || null,
    work_period: form.work_period || null,
    started_at: touchedTimesRef.current.started_at ? isoOf(form.started_at) : null,
    ended_at: touchedTimesRef.current.ended_at ? isoOf(form.ended_at) : null
  });
  const outcomePartOf = (form) => ({
    outcome: form.outcome || null,
    completion_summary: form.completion_summary || '',
    assign_next_type: form.assign_next_type || '',
    assigned_to_next_id: form.assigned_to_next_id || null,
    follow_up_ticket_required: Boolean(form.follow_up_ticket_required),
    follow_up_subject: form.follow_up_subject || '',
    follow_up_description: form.follow_up_description || '',
    follow_up_priority: form.follow_up_priority || '',
    follow_up_asset_id: form.follow_up_asset_id || null
  });
  const saveFormParts = (fields, form) => {
    const keys = [...new Set(fields.map(field => FORM_FIELD_PART[field]).filter(Boolean))];
    keys.forEach(key => {
      if (key === 'work') persist('work', workPartOf(form));
      else if (key === 'notes') persist('notes', { notes: form.notes || '' });
      else persist('outcome', outcomePartOf(form));
    });
  };
  /** Changes the form; with the saved Log Work live, the engineer's change is saved too. */
  const applyForm = (patch, { save = true } = {}) => {
    const next = { ...formRef.current, ...patch };
    formRef.current = next;
    setFormData(next);
    if (save && live) saveFormParts(Object.keys(patch), next);
    return next;
  };

  // Daily Van Check — the tasks API requires it whenever Work Type is Site
  // Work (same rule as tickets since R4a); without this form site-work logs
  // 400'd and jobs could never be signed off.
  const [dailyVanCheck, setDailyVanCheck] = useState(() => {
    if (!restore) return { ...emptyDailyVanCheck };
    return {
      ...emptyDailyVanCheck,
      ...(restore.van || {}),
      damage_photo_attachments: restore.vanPhotos
    };
  });
  const changeVanCheck = (next) => {
    setDailyVanCheck(next);
    if (live) persist('van_check', withoutPhotos(next));
  };

  // Job Stages (site work): parent holds the travel record so its id can be
  // linked into the work-log payload on save
  const [travelRecord, setTravelRecord] = useState(() => (restore ? restore.travelRecord : null));
  const restoredJourney = Boolean(restore && restore.travelRecord);
  // Saved Log Work: an earlier trip today (saved with Log Work, or over) is
  // shown by the top card and is not the trip being logged.
  const [previousTrip, setPreviousTrip] = useState(null);
  const travelRef = useRef(travelRecord);
  travelRef.current = travelRecord;
  // A record the card reports after a tap in it is the engineer's own stamp;
  // one it reports on opening is only what was already there (never saved).
  const stampIntentRef = useRef(false);
  const markStampIntent = () => { stampIntentRef.current = true; };
  // Saved Log Work: the restored journey's times come from the record itself,
  // read on opening. saved.journeys is only as fresh as the last GET of the
  // saved Log Work, so a stamp made since (or a journey part not yet sent)
  // would show as Not recorded, and a second tap would move a paid time
  // later. Until the record is read, stamping waits; a failed read is tried
  // again.
  const restoredJourneyId = restore && restore.travelRecord ? restore.travelRecord.travel_id : null;
  const [journeyLoaded, setJourneyLoaded] = useState(!restoredJourneyId);
  const recordChangesRef = useRef(0);
  useEffect(() => {
    if (!restoredJourneyId) return undefined;
    let cancelled = false;
    let timer = null;
    let attempt = 0;
    const load = async () => {
      const changesAtStart = recordChangesRef.current;
      let done = false;
      try {
        const response = await authFetch(`/api/v1/travel/${encodeURIComponent(restoredJourneyId)}`);
        if (cancelled) return;
        if (response && response.ok) {
          const data = await response.json().catch(() => null);
          if (cancelled) return;
          const record = data && data.travel_id ? data : (data && data.data && data.data.travel_id ? data.data : null);
          const stillHeld = lowerId(travelRef.current && travelRef.current.travel_id) === lowerId(restoredJourneyId);
          if (record && lowerId(record.travel_id) === lowerId(restoredJourneyId)
            && stillHeld && recordChangesRef.current === changesAtStart) {
            travelRef.current = record;
            setTravelRecord(record);
          }
          done = true;
        } else if (response && response.status >= 400 && response.status < 500) {
          // Gone or not ours: nothing more to read; the saved times stand.
          done = true;
        }
      } catch (err) {
        // No signal: tried again below.
      }
      if (cancelled) return;
      if (done) {
        setJourneyLoaded(true);
        return;
      }
      const delay = JOURNEY_RETRY_MS[Math.min(attempt, JOURNEY_RETRY_MS.length - 1)];
      attempt += 1;
      timer = setTimeout(load, delay);
    };
    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [authFetch, restoredJourneyId]);

  const handleTravelRecordChange = (record) => {
    recordChangesRef.current += 1;
    travelRef.current = record;
    setTravelRecord(record);
    if (!live || !record || !record.travel_id || record.work_log_id || !stampIntentRef.current) return;
    const parts = (savedRef.current && savedRef.current.parts) || {};
    const current = parts[journeyKey] && parts[journeyKey].value ? parts[journeyKey].value.travel_id : null;
    if (lowerId(current) !== lowerId(record.travel_id)) {
      persist(journeyKey, { travel_id: record.travel_id }, { immediate: true });
    }
  };
  const handlePreviousTrip = (record) => {
    if (!record) return;
    // A finished trip of the engineer's own that is in no hours, found on
    // opening, is still the one to log (it is not an earlier trip until it
    // is filed).
    const mine = ((savedRef.current && savedRef.current.myUnfiledJourneys) || [])
      .some(row => lowerId(row.travel_id) === lowerId(record.travel_id));
    if (!stampIntentRef.current && !travelRef.current && !record.work_log_id && mine) {
      travelRef.current = record;
      setTravelRecord(record);
      return;
    }
    setPreviousTrip(record);
  };
  const isSiteWork = formData.log_type === 'site_work';

  // Other engineers on this job (Phase 4, Gareth's 11 Sep crew rules): each
  // one gets their OWN work log with these times, left open for them to
  // confirm or correct, and is told straight away.
  // Phase 5: filled in once, when the modal opens, from who is booked on the
  // job (task.engineers) less the person logging; still editable, so someone
  // who was not there on the day gets no hours. Only the booked list counts -
  // a job with no list starts empty, as before.
  // Left out of the pre-fill, because the save would be refused for them:
  // anyone booked who has since been deactivated (only active engineers can
  // be given hours), and anyone who still has hours of their own on this job
  // to confirm (the server treats an open log as covering any time, so filing
  // again for them is refused; they confirm their own hours instead).
  // Q3 (saved Log Work live): only an open entry that touches today's UK day
  // is in the way, so only those engineers are left out.
  const bookedCrew = (Array.isArray(task?.engineers) ? task.engineers : [])
    .filter(engineer => engineer && engineer.user_id);
  const jobWorkLogs = Array.isArray(task?.work_logs) ? task.work_logs : [];
  // Only someone who is booked on the job gets the crew filled in. A manager
  // or a stand-in logging work was not necessarily with the booked crew, and a
  // co-engineer's entry counts towards their hours as soon as it exists, so
  // they name whoever was actually there themselves.
  const viewerIsBooked = Boolean(currentUserId) && (task?.assigned_to_id === currentUserId
    || bookedCrew.some(engineer => engineer.user_id === currentUserId));
  const prefillFor = (activeEngineers) => {
    const activeIds = activeEngineers && activeEngineers.length > 0
      ? new Set(activeEngineers.map(engineer => engineer.user_id))
      : null;
    return (bookedCrew.length > 0 && viewerIsBooked ? engineersOnJob(task) : [])
      .map(engineer => engineer.user_id)
      .filter(engineerId => engineerId !== currentUserId)
      .filter(engineerId => !jobWorkLogs.some(log => log.engineer_id === engineerId && !log.outcome
        && (!live || touchesUkToday(log))))
      .filter(engineerId => !activeIds || activeIds.has(engineerId));
  };
  // Saved Log Work: the crew part names everyone on this visit, the author
  // included; the window shows the others. The booking pre-fill applies only
  // when there is no crew part, and is not saved until it is changed.
  const [restoredCrewIds] = useState(() => (restore && restore.crew && Array.isArray(restore.crew.engineer_ids)
    ? restore.crew.engineer_ids.filter(engineerId => lowerId(engineerId) !== meKey)
    : null));
  const [additionalEngineerIds, setAdditionalEngineerIds] = useState(() => restoredCrewIds || prefillFor(engineers));
  const [prefilledFromBooking] = useState(() => !restoredCrewIds && additionalEngineerIds.length > 0);
  const [notOnVisitIds, setNotOnVisitIds] = useState(() => (restore && restore.crew && Array.isArray(restore.crew.not_on_visit_ids)
    ? restore.crew.not_on_visit_ids.map(lowerId)
    : []));
  const [droppedCrewNotice, setDroppedCrewNotice] = useState(null);
  const saveCrew = (ids, notOnVisit) => persist('crew', {
    engineer_ids: [currentUserId, ...ids.filter(engineerId => lowerId(engineerId) !== meKey)].filter(Boolean),
    not_on_visit_ids: notOnVisit
  });
  const changeCrew = (ids) => {
    setAdditionalEngineerIds(ids);
    if (live) saveCrew(ids, notOnVisitIds);
  };
  const toggleNotOnVisit = (userId, checked) => {
    const next = checked
      ? [...new Set([...notOnVisitIds, lowerId(userId)])]
      : notOnVisitIds.filter(engineerId => engineerId !== lowerId(userId));
    setNotOnVisitIds(next);
    saveCrew(additionalEngineerIds, next);
  };
  // The active engineers list can arrive after the modal opens: once it does,
  // take anyone no longer active out of the pre-fill (once only, so later
  // edits by the person logging are left alone).
  const prefillCheckedRef = useRef(Boolean(restoredCrewIds) || (Array.isArray(engineers) && engineers.length > 0));
  useEffect(() => {
    if (prefillCheckedRef.current || !Array.isArray(engineers) || engineers.length === 0) return;
    prefillCheckedRef.current = true;
    const activeIds = new Set(engineers.map(engineer => engineer.user_id));
    setAdditionalEngineerIds(ids => {
      const kept = ids.filter(engineerId => activeIds.has(engineerId));
      return kept.length === ids.length ? ids : kept;
    });
  }, [engineers]);
  // A restored crew with someone since deactivated: they are dropped, said so,
  // and the crew part saved without them (filing would refuse otherwise).
  const restoredCrewCheckedRef = useRef(!restoredCrewIds);
  useEffect(() => {
    if (restoredCrewCheckedRef.current || !Array.isArray(engineers) || engineers.length === 0) return;
    restoredCrewCheckedRef.current = true;
    const activeIds = new Set(engineers.map(engineer => lowerId(engineer.user_id)));
    const dropped = additionalEngineerIds.filter(engineerId => !activeIds.has(lowerId(engineerId)));
    if (dropped.length === 0) return;
    const kept = additionalEngineerIds.filter(engineerId => activeIds.has(lowerId(engineerId)));
    const names = dropped.map(engineerId => {
      const booked = bookedCrew.find(engineer => lowerId(engineer.user_id) === lowerId(engineerId));
      const part = Object.values((restore && restore.parts) || {})
        .find(entry => entry && lowerId(entry.changed_by) === lowerId(engineerId) && entry.changed_by_name);
      return (booked && engineerNameOf(booked)) || (part && part.changed_by_name) || 'An engineer';
    });
    setAdditionalEngineerIds(kept);
    setDroppedCrewNotice(names.length === 1
      ? `${names[0]} is no longer an active engineer, so was taken off the crew.`
      : `${listJoin(names)} are no longer active engineers, so were taken off the crew.`);
    saveCrew(kept, notOnVisitIds);
    // Runs once, when the active engineers list is first known.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engineers]);
  const bookedCrewNames = Object.fromEntries(bookedCrew.map(engineer => [engineer.user_id, engineerNameOf(engineer)]));
  const coEngineerOptions = (engineers || []).filter(engineer => engineer.user_id && engineer.user_id !== currentUserId);
  // Saved Log Work: who started it is named on the crew list.
  const authorId = live && saved.draft ? lowerId(saved.draft.created_by) : null;
  const authorName = live && saved.draft ? saved.draft.created_by_name : null;
  const authorIsMe = Boolean(authorId) && authorId === meKey;
  const authorMark = ' (started this Log Work)';
  const pickerOptions = authorId && !authorIsMe
    ? coEngineerOptions.map(engineer => (lowerId(engineer.user_id) === authorId
      ? { ...engineer, full_name: `${engineer.full_name}${authorMark}` }
      : engineer))
    : coEngineerOptions;
  const pickerNames = authorId && !authorIsMe && authorName
    ? { ...bookedCrewNames, ...Object.fromEntries(additionalEngineerIds
      .filter(engineerId => lowerId(engineerId) === authorId)
      .map(engineerId => [engineerId, `${authorName}${authorMark}`])) }
    : bookedCrewNames;

  // Van seats follow the CREW: only the people on this job can be recorded in
  // its van (the server refuses anyone else). The viewer comes first, named
  // from the engineers list, or from their own account when not on it.
  const vanCrew = useMemo(() => {
    const list = engineers || [];
    const me = currentUserId
      ? (list.find(engineer => engineer.user_id === currentUserId)
        || { user_id: currentUserId, full_name: currentUser?.fullName || currentUser?.full_name || 'Me' })
      : null;
    const others = additionalEngineerIds
      .map(engineerId => list.find(engineer => engineer.user_id === engineerId))
      .filter(Boolean);
    return [...(me ? [me] : []), ...others];
  }, [engineers, currentUserId, currentUser, additionalEngineerIds]);

  // The van list is only needed to count seats once there is a crew: with the
  // filer alone one van always fits (DailyVanCheckSection loads its own list).
  const [vehicles, setVehicles] = useState([]);
  const hasCrew = additionalEngineerIds.length > 0;
  useEffect(() => {
    if (!isSiteWork || !hasCrew) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await authFetch('/api/v1/tickets/active-vehicles');
        if (!response.ok) return;
        const data = await response.json().catch(() => ({}));
        if (!cancelled) setVehicles(Array.isArray(data.data) ? data.data : []);
      } catch (err) {
        console.error('Failed to load vehicles:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch, isSiteWork, hasCrew]);

  // More engineers than seats means a second van, checked by whichever of
  // them drove it - unless they already checked that van earlier today, which
  // only the server can tell; it says so, and the check opens here.
  const seatCountOf = (vehicle) => Number(vehicle?.number_of_seats) || 0;
  const selectedVehicle = vehicles.find(vehicle => vehicle.vehicle_id === dailyVanCheck.vehicle_id);
  const seatCount = seatCountOf(selectedVehicle);
  const needsSecondVan = isSiteWork && Boolean(selectedVehicle) && seatCount > 0 && vanCrew.length > seatCount;
  const seatedIds = (dailyVanCheck.seat_selections || []).filter(Boolean);
  const [secondVan, setSecondVan] = useState(() => (restore && restore.second
    ? { vehicle_id: restore.second.vehicle_id || '', driver_id: restore.second.driver_id || '' }
    : { vehicle_id: '', driver_id: '' }));
  const [secondVanCheckOpen, setSecondVanCheckOpen] = useState(() => Boolean(restore && restore.second && restore.second.check_open));
  const [secondVanCheck, setSecondVanCheck] = useState(() => (restore && restore.second
    ? { ...emptyDailyVanCheck, ...secondVanCheckOf(restore.second), damage_photo_attachments: restore.secondPhotos }
    : { ...emptyDailyVanCheck }));
  const secondVanRef = useRef(null);
  secondVanRef.current = { van: secondVan, open: secondVanCheckOpen, check: secondVanCheck };
  // Saved Log Work: the second van, its driver, whether its check is open and
  // the check itself are one part.
  const saveSecondVan = (patch) => {
    if (!live) return;
    const next = { ...secondVanRef.current, ...patch };
    secondVanRef.current = next;
    persist('second_van', {
      ...withoutPhotos(next.check),
      vehicle_id: next.van.vehicle_id || '',
      driver_id: next.van.driver_id || '',
      check_open: Boolean(next.open)
    });
  };
  const secondVanCrew = vanCrew.filter(engineer => !seatedIds.includes(engineer.user_id));
  const secondVanDriverName = vanCrew.find(engineer => engineer.user_id === secondVan.driver_id)?.full_name || 'the driver';
  const chooseSecondVan = (vehicleId) => {
    const van = { ...secondVanRef.current.van, vehicle_id: vehicleId };
    const check = { ...secondVanRef.current.check, vehicle_id: vehicleId, seat_selections: [] };
    setSecondVan(van);
    setSecondVanCheck(check);
    saveSecondVan({ van, check });
  };
  const chooseSecondVanDriver = (driverId) => {
    const van = { ...secondVanRef.current.van, driver_id: driverId };
    setSecondVan(van);
    saveSecondVan({ van });
  };
  const openSecondVanCheck = (open) => {
    setSecondVanCheckOpen(open);
    saveSecondVan({ open });
  };
  const changeSecondVanCheck = (next) => {
    setSecondVanCheck(next);
    let { van } = secondVanRef.current;
    if (next.vehicle_id !== van.vehicle_id) {
      van = { ...van, vehicle_id: next.vehicle_id };
      setSecondVan(van);
    }
    saveSecondVan({ van, check: next });
  };

  // Check forms: one section per robot on the job, each owning its own
  // check-form state (multi-asset jobs, Phase 2). The list comes from
  // task.assets, primary robot first; an older fixture with no assets list
  // falls back to the job's single asset; a job with no listed robot has no
  // check section at all. A sales_action task keeps exactly one section for
  // its stage-targeted sales form, keyed on the task rather than a robot,
  // even when the task carries the minted asset.
  const isSalesActionTask = task?.task_type === 'sales_action';
  const jobRobots = useMemo(() => (isSalesActionTask ? [] : robotsOnJob(task)), [isSalesActionTask, task]);
  // Phase 6 step 4: with several robots each faulty robot raises its own
  // follow-up ticket in its own section, and the job-level box is hidden.
  const followUpPerRobot = jobRobots.length > 1;
  const checkSections = useMemo(() => {
    if (isSalesActionTask) {
      return [{
        key: 'task',
        asset: { asset_id: task?.asset_id || null, asset_ref: task?.asset_ref || null, robot_type: task?.robot_type }
      }];
    }
    return jobRobots.map(row => ({
      key: row.asset_id,
      asset: { asset_id: row.asset_id, asset_ref: row.asset_ref || null, robot_type: row.robot_type }
    }));
  }, [isSalesActionTask, task, jobRobots]);
  const taskTypeLabel = getTaskTypeLabel(task?.task_type);
  const sectionAssetRefs = useMemo(() => Object.fromEntries(checkSections
    .filter(section => section.asset.asset_id && section.asset.asset_ref)
    .map(section => [lowerId(section.asset.asset_id), section.asset.asset_ref])), [checkSections]);
  const sectionForPart = (partKey) => checkSections.find(section => partKeyOfSection(section.key) === partKey) || null;

  // Phase 3: each robot's sign-off for this visit - Done / Not done, a reason
  // when not done, an optional health score - keyed by asset_id. A robot with
  // no entry yet is Done with nothing recorded. The sales task section has no
  // sign-off: it is not a robot. Saved Log Work: restored from the robot parts.
  const [robotOutcomes, setRobotOutcomes] = useState(() => {
    if (!restore) return {};
    const restored = {};
    checkSections.forEach(section => {
      if (section.key === 'task') return;
      const part = restore.parts[partKeyOfSection(section.key)];
      if (part && part.value) restored[section.key] = robotOutcomeFromPart(part.value);
    });
    return restored;
  });
  const robotOutcomeFor = (assetId) => robotOutcomes[assetId] || emptyRobotOutcome();
  const patchRobotOutcome = (assetId, patch) => setRobotOutcomes(prev => ({
    ...prev,
    [assetId]: { ...(prev[assetId] || emptyRobotOutcome()), ...patch }
  }));
  // Saved Log Work: a section another phone moved on (and this one has not
  // changed) is mounted again on the newer part.
  const [sectionVersions, setSectionVersions] = useState({});

  // Each section reports its check-form state here as it changes; submit
  // reads the map, so no re-render of the modal is needed for it.
  const checkSectionStateRef = useRef({});
  const handleCheckSectionState = useCallback((sectionKey, state) => {
    if (state === null) {
      delete checkSectionStateRef.current[sectionKey];
    } else {
      checkSectionStateRef.current[sectionKey] = state;
    }
  }, []);
  // Saved Log Work wiring for the sections (stable, so the forms' own hooks
  // are not disturbed on every render).
  const handlePartChange = useCallback((sectionKey, change) => {
    if (savedRef.current) savedRef.current.savePart(partKeyOfSection(sectionKey), change);
  }, []);
  const removeUploaded = useCallback(
    (attachment) => savedRef.current.removeFile(attachment.file_id || attachment.client_ref),
    []
  );
  const uploadersRef = useRef({});
  const uploaderFor = (partKey) => {
    if (!uploadersRef.current[partKey]) {
      uploadersRef.current[partKey] = partKey === 'van_check' || partKey === 'second_van'
        ? (file) => savedRef.current.uploadFile({ partKey, file })
        : (stepKey, file) => savedRef.current.uploadFile({ partKey, stepKey, file });
    }
    return uploadersRef.current[partKey];
  };
  const savedSectionFor = (sectionKey) => {
    const partKey = partKeyOfSection(sectionKey);
    const part = saved.parts[partKey];
    return {
      form: part && part.value ? part.value.form || null : null,
      files: [
        ...saved.files.filter(file => file.part_key === partKey),
        ...saved.photos.filter(photo => photo.part_key === partKey && photo.state !== 'failed')
          .map(photo => ({ client_ref: photo.client_ref, step_key: photo.step_key, name: photo.name, size: photo.size, type: photo.type }))
      ]
    };
  };

  // Parts used (spare parts, spec 6.4): one spare:<uuid> part per line, read
  // straight from the hook, which already merges this phone's unsent lines
  // and the 60-second poll; never copied into this window's own state. Only
  // a pick, a change or a removal saves: opening the window saves nothing.
  const savedParts = saved ? saved.parts : null;
  const spareLineEntries = useMemo(() => Object.entries(savedParts || {})
    .filter(([key, part]) => isSparePartKey(key) && part && part.value)
    .map(([key, part]) => ({
      key, value: part.value, pending: Boolean(part.pending), failed: Boolean(part.failed), conflict: Boolean(part.conflict)
    }))
    .sort(byAddedAt), [savedParts]);
  const spareRobots = useMemo(() => jobRobots
    .map(robot => ({ asset_id: lowerId(robot.asset_id), asset_ref: robot.asset_ref })), [jobRobots]);
  // The sentence for each line whose quantity box holds text that cannot be
  // read, so Complete Work Log stops on it rather than filing the old quantity.
  const spareTextProblemsRef = useRef({});
  // Only roles that record parts (canFitParts, the mirror of the API's
  // PARTS_FIT) get the section: saved Log Work refuses a Parts used line
  // from any other role. The job page's own Parts used list is for everyone.
  const canRecordParts = canFitParts(currentUser?.role);
  const addSpareLine = (picked) => {
    if (!picked || !canRecordParts) return;
    persist(`spare:${lowerId(newUuid())}`, {
      catalogue_id: picked.notInList ? null : lowerId(picked.id),
      item_number: picked.notInList ? null : (picked.number || null),
      name: picked.name,
      quantity: 1,
      asset_id: null,
      whole_job: false,
      not_in_list: Boolean(picked.notInList),
      added_by: meKey,
      added_at: new Date().toISOString()
    });
  };
  // Who added a line: the engineers list, then the viewer, else 'a colleague'.
  const nameOfUser = (userId) => {
    const key = lowerId(userId);
    if (!key) return 'a colleague';
    const listed = (engineers || []).find(engineer => lowerId(engineer.user_id) === key);
    if (listed && (listed.full_name || listed.preferred_name)) return listed.full_name || listed.preferred_name;
    if (key === meKey) return currentUser?.fullName || currentUser?.full_name || 'a colleague';
    return 'a colleague';
  };

  const closesWork = outcomeClosesWork(config, formData.outcome);
  // No outcome yet: the log is in progress, and the main button saves it
  // part-way instead of completing it (as the ticket window does).
  const hasOutcome = Boolean(formData.outcome);
  const carryOn = storeUp && formData.assign_next_type === 'carry_on';

  // Saved Log Work, reconciled on opening (spec 6.4): answers given on a
  // form that no longer fits this visit (another job type or work type) are
  // shown read-only, and a fresh form is resolved for the robot.
  const workTypeLabelOf = (value) => (config.work_types || []).find(type => type.value === value)?.label || null;
  const [mismatchedAtOpen] = useState(() => {
    if (!restore) return [];
    const openedLogType = (restore.work && restore.work.log_type) || formData.log_type;
    return checkSections
      .map(section => ({ section, part: restore.parts[partKeyOfSection(section.key)] }))
      .filter(({ part }) => part && part.value && hasFormAnswers(part.value) && part.value.form.template_id
        && !savedFormMatches(part.value.form, { workLogType: openedLogType, taskType: task?.task_type }))
      .map(({ section, part }) => ({
        id: `ref:${section.key}`,
        asset: section.asset,
        form: part.value.form,
        reason: `Answers for the ${workTypeLabelOf(part.value.form.log_type) || getTaskTypeLabel(part.value.form.task_type) || 'earlier'} form, kept for reference. They will not be filed.`
      }));
  });
  const referenceSections = [];
  if (live) {
    referenceSections.push(...mismatchedAtOpen);
    Object.entries(saved.parts).forEach(([key, part]) => {
      if (!part || !part.value) return;
      if (key.startsWith('robot:') && !sectionForPart(key) && hasFormAnswers(part.value)) {
        const assetId = key.slice(6);
        const label = sectionAssetRefs[assetId] || 'A robot';
        referenceSections.push({
          id: `off:${assetId}`,
          asset: { asset_id: assetId, asset_ref: sectionAssetRefs[assetId] || null, robot_type: null },
          form: part.value.form,
          reason: `${label} was taken off this job, so its answers are kept but will not be filed.`
        });
      }
      if (key.startsWith('archive:') && part.value.value && hasFormAnswers(part.value.value)) {
        const archivedKey = part.value.part_key || 'form:task';
        const section = sectionForPart(archivedKey);
        const assetId = archivedKey.startsWith('robot:') ? archivedKey.slice(6) : null;
        const label = partLineLabel(archivedKey, sectionAssetRefs);
        referenceSections.push({
          id: key,
          asset: section ? section.asset : { asset_id: assetId, asset_ref: assetId ? sectionAssetRefs[assetId] || null : null, robot_type: null },
          form: part.value.value.form,
          reason: `${label} - answers by ${part.changed_by_name || 'a colleague'}, kept for reference${part.value.why ? ` (${part.value.why})` : ''}. They will not be filed.`
        });
      }
    });
  }

  // Saved Log Work: a colleague's change (or this phone's copy reaching the
  // server) is put on the screen, part by part, unless this phone has its own
  // unsent change to that part.
  const applyServerParts = (keys, { includeLocal = false } = {}) => {
    const current = savedRef.current;
    if (!live || !current) return;
    const parts = current.parts || {};
    let form = null;
    (keys || []).forEach(key => {
      const part = parts[key];
      if (!part || (part.local && !includeLocal)) return;
      const { value } = part;
      if (key === 'work') {
        if (!value) return;
        if (value.started_at) touchedTimesRef.current.started_at = true;
        if (value.ended_at) touchedTimesRef.current.ended_at = true;
        form = formFromParts(form || formRef.current, { work: value }, meKey);
      } else if (key === 'notes') {
        form = { ...(form || formRef.current), notes: value && typeof value.notes === 'string' ? value.notes : '' };
      } else if (key === 'outcome') {
        if (value) form = formFromParts(form || formRef.current, { outcome: value }, meKey);
      } else if (key === 'crew') {
        const ids = value && Array.isArray(value.engineer_ids) ? value.engineer_ids : [];
        setAdditionalEngineerIds(ids.filter(engineerId => lowerId(engineerId) !== meKey));
        setNotOnVisitIds(value && Array.isArray(value.not_on_visit_ids) ? value.not_on_visit_ids.map(lowerId) : []);
      } else if (key === 'van_check') {
        setDailyVanCheck({ ...emptyDailyVanCheck, ...(value || {}), damage_photo_attachments: photosOfPart(current, 'van_check') });
      } else if (key === 'second_van') {
        const van = { vehicle_id: (value && value.vehicle_id) || '', driver_id: (value && value.driver_id) || '' };
        const check = { ...emptyDailyVanCheck, ...secondVanCheckOf(value), damage_photo_attachments: photosOfPart(current, 'second_van') };
        setSecondVan(van);
        setSecondVanCheckOpen(Boolean(value && value.check_open));
        setSecondVanCheck(check);
        secondVanRef.current = { van, open: Boolean(value && value.check_open), check };
      } else if (isFormPartKey(key)) {
        const section = sectionForPart(key);
        if (!section) return;
        if (section.key !== 'task' && value) {
          setRobotOutcomes(prev => ({ ...prev, [section.key]: robotOutcomeFromPart(value) }));
        }
        setSectionVersions(prev => ({ ...prev, [section.key]: (prev[section.key] || 0) + 1 }));
      }
    });
    if (form) {
      formRef.current = form;
      setFormData(form);
    }
  };
  // Photos added or removed elsewhere (a colleague, or my other phone) change
  // no part: the van checks take the saved Log Work's photos again, and a
  // check form section is mounted again on them.
  const applyRemoteFiles = (keys) => {
    const current = savedRef.current;
    if (!live || !current) return;
    (keys || []).forEach(key => {
      if (key === 'van_check') {
        const photos = photosOfPart(current, 'van_check');
        setDailyVanCheck(prev => ({ ...prev, damage_photo_attachments: photos }));
      } else if (key === 'second_van') {
        const check = { ...secondVanRef.current.check, damage_photo_attachments: photosOfPart(current, 'second_van') };
        secondVanRef.current = { ...secondVanRef.current, check };
        setSecondVanCheck(check);
      } else if (isFormPartKey(key)) {
        const section = sectionForPart(key);
        if (section) setSectionVersions(prev => ({ ...prev, [section.key]: (prev[section.key] || 0) + 1 }));
      }
    });
  };
  const remoteAt = live && saved.remoteChanges ? saved.remoteChanges.at : null;
  const seenRemoteAtRef = useRef(remoteAt);
  useEffect(() => {
    if (!remoteAt || remoteAt === seenRemoteAtRef.current) return;
    seenRemoteAtRef.current = remoteAt;
    const changes = savedRef.current && savedRef.current.remoteChanges;
    applyServerParts(changes ? changes.keys : []);
    applyRemoteFiles(changes ? changes.fileKeys : []);
    // Runs when the hook reports a colleague's change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteAt]);
  // Opened with no signal: once the server answers, what it has (and this
  // phone did not change) goes on the screen.
  const liveStatus = live ? saved.status : null;
  const openedOfflineRef = useRef(live && savedLogWork.status === 'offline');
  useEffect(() => {
    if (!openedOfflineRef.current || liveStatus !== 'ready') return;
    openedOfflineRef.current = false;
    applyServerParts(Object.keys((savedRef.current && savedRef.current.parts) || {}));
    // Runs once, when the first answer arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveStatus]);

  // A work type change while a robot has check form answers: the answers are
  // set aside by the server (kept for reference, never filed), so it is asked
  // first (WORK_TYPE_CLEARS).
  const answeredFormsAgainst = (newLogType) => Object.entries((savedRef.current && savedRef.current.parts) || {})
    .filter(([key, part]) => isFormPartKey(key) && part && hasFormAnswers(part.value)
      && (part.value.form.log_type || formRef.current.log_type) !== newLogType)
    .map(([key, part]) => `${partLineLabel(key, sectionAssetRefs)} (${part.changed_by_name || 'you'})`);
  const askWorkTypeChange = (newLogType, named) => {
    requestConfirm({
      title: 'Change the work type?',
      message: `Changing the work type clears the check form answers for ${listJoin(named)}. They are kept for reference but not filed. Change it anyway?`,
      confirmLabel: 'Change it',
      cancelLabel: 'Keep the work type',
      onConfirm: async () => {
        const current = savedRef.current;
        try {
          await current.flush();
          const next = applyForm({ log_type: newLogType }, { save: false });
          await current.savePart('work', workPartOf(next), { clearsRobotAnswers: true, immediate: true });
          await current.refresh();
        } catch (err) {
          setError(err.message);
        }
      }
    });
  };

  const stageStampTime = (value) => value
    ? new Date(value).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '—';

  // Saved Log Work: each picked file is sent into the saved Log Work on its
  // own and listed below; a new pick adds to the list.
  const addSavedFiles = async (picker) => {
    const files = Array.from(picker.files || []);
    try { picker.value = ''; } catch (err) { /* some browsers refuse; the list below is what counts */ }
    const errors = await uploadFilesOneByOne(files, file => savedRef.current.uploadFile({ partKey: 'attachments', file }), () => {});
    if (errors.length > 0) setError(errors.join(' '));
  };

  const handleChange = async (event) => {
    const { name, value, type, files } = event.target;
    if (type === 'file') {
      const picker = event.target;
      if (storeUp) {
        await addSavedFiles(picker);
        return;
      }
      try {
        const attachments = await filesToAttachmentPayload(files);
        setFormData(prev => ({ ...prev, attachments }));
      } catch (err) {
        // Refused files are not attached: the picker is emptied so it cannot go on
        // showing files that will not be saved.
        picker.value = '';
        setFormData(prev => ({ ...prev, attachments: savedAttachmentsOnly(prev.attachments) }));
        setError(err.message);
      }
      return;
    }
    if (name === 'started_at' || name === 'ended_at') {
      touchedTimesRef.current = { ...touchedTimesRef.current, [name]: true };
    }
    if (live && name === 'log_type' && value !== formRef.current.log_type) {
      const named = answeredFormsAgainst(value);
      if (named.length > 0) {
        askWorkTypeChange(value, named);
        return;
      }
    }
    applyForm({
      [name]: value,
      ...(name === 'assign_next_type' && value !== 'user' ? { assigned_to_next_id: '' } : {})
    });
  };

  // Saved Log Work: my Job Stages from an earlier UK day (a visit over two
  // days saved only with Save Progress). Closing is held until Carry on is
  // chosen or the engineer ticks that they did not work here today.
  const journeyDayKey = live && travelRecord ? ukDayKeyOf(travelRecord.departed_at) : null;
  const earlierDayKey = journeyDayKey && journeyDayKey < ukTodayKey() ? journeyDayKey : null;
  const [notWorkedToday, setNotWorkedToday] = useState(false);
  const earlierDayBlocks = Boolean(earlierDayKey) && closesWork && !notWorkedToday;
  const earlierDaySentence = earlierDayKey
    ? `Your Job Stages here are from ${ukDayLabelOfKey(earlierDayKey)}. If you are working on this job today, save ${ukWeekdayOfKey(earlierDayKey, 'long') || 'that day'} first with Carry on tomorrow, then start today's Job Stages.`
    : null;

  // Saved Log Work: the draft this window works on (the one it opened on, or
  // the first one it learns of), a Complete Work Log whose reply was lost, and
  // the draft being saved or cleared elsewhere (the window then stops).
  const trackedDraftIdRef = useRef(live && savedLogWork ? savedLogWork.draftId || null : null);
  const lostFilingRef = useRef(null);
  const filedRef = useRef(false);
  const submittingRef = useRef(false);
  const [closedDraft, setClosedDraft] = useState(null);

  const hasUnsentChanges = () => {
    const current = savedRef.current || {};
    return Object.values(current.parts || {}).some(part => part && part.local)
      || (Array.isArray(current.photos) && current.photos.length > 0);
  };

  /** Filed (or already filed by this window): the phone's copy goes and the window closes. */
  const finishFiled = async (data, carryOnSent) => {
    filedRef.current = true;
    lostFilingRef.current = null;
    // Phase 6 step 4: the follow-up tickets this save raised, one per faulty
    // robot (linked_follow_up_tickets), or the single job-level one.
    const raisedTickets = Array.isArray(data.linked_follow_up_tickets) && data.linked_follow_up_tickets.length > 0
      ? data.linked_follow_up_tickets
      : (data.linked_follow_up_ticket ? [data.linked_follow_up_ticket] : []);
    if (live) {
      try { await savedRef.current.markFiled(); } catch (err) { /* found stale next time, never filed twice */ }
    }
    // B4: the API says when a call-out lands in a month that has already been
    // paid, and where the money goes instead. Spare parts (spec 6.4): Parts
    // used lines an earlier Log Work recorded were skipped, and are named.
    const alreadyRecorded = Array.isArray(data.spare_parts_already_recorded)
      ? data.spare_parts_already_recorded.filter(Boolean)
      : [];
    const notices = [
      data.payroll_notice || null,
      data.already_saved ? data.message || null : null,
      carryOnSent ? CARRY_ON_SAVED_MESSAGE : null,
      alreadyRecorded.length > 0 ? spareAlreadyRecordedNotice(alreadyRecorded) : null
    ].filter(Boolean);
    onSaved(notices.length > 0 ? notices.join(' ') : null, raisedTickets.filter(ticket => ticket && (ticket.ticket_ref || ticket.ticket_id)));
  };

  /**
   * POST Complete Work Log. No reply at all (no signal) on a saved Log Work keeps
   * the exact body, so the next tap sends it again and is never filed twice.
   */
  const sendFiling = async (body, { draftId = null, carryOnSent = false } = {}) => {
    let response;
    try {
      response = await authFetch(`/api/v1/tasks/${taskId}/work-log`, {
        method: 'POST',
        body: JSON.stringify(body)
      });
    } catch (err) {
      if (live && draftId) {
        lostFilingRef.current = { body, draftId, carryOn: carryOnSent };
        throw new Error(LOST_REPLY_MESSAGE);
      }
      throw err;
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      lostFilingRef.current = null;
      // The driver has not checked the second van today: open its check
      // so it can be filled in and saved again.
      if (needsSecondVan && /second van needs its own daily van check/i.test(data.message || '')) {
        setSecondVanCheckOpen(true);
      }
      // The saved Log Work moved on: the screen is refreshed and this
      // phone's copy kept.
      if (live && LOG_WORK_FILING_CODES.includes(data.code)) {
        try { await savedRef.current.refresh(); } catch (err) { /* the sentence still shows */ }
      }
      throw new Error(data.message || 'Failed to save work log');
    }
    await finishFiled(data, carryOnSent);
  };

  /**
   * After a refresh: the window's draft saved or cleared elsewhere stops the
   * window with the server's facts; saved by this window's own lost Save Work
   * Log, it is filed. True when the save must go no further.
   */
  const stopIfClosed = async () => {
    await afterRender();
    const closed = closedDraftOf(savedRef.current, trackedDraftIdRef.current, meKey);
    if (!closed) return false;
    const lost = lostFilingRef.current;
    if (closed.savedByMe && lost && lowerId(lost.draftId) === lowerId(closed.draftId)) {
      await finishFiled({ already_saved: true, message: `You saved Log Work for this job ${logWorkAtWhen(closed.savedAt)}.` }, lost.carryOn);
      return true;
    }
    setClosedDraft(closed);
    return true;
  };

  // A poll finding the window's draft saved or cleared stops the window too.
  const liveDraftId = live ? saved.draftId : null;
  const liveLastSavedId = live ? saved.lastSavedId : null;
  const liveReadyStatus = live ? saved.status : null;
  useEffect(() => {
    if (!live || closedDraft || filedRef.current || submittingRef.current) return;
    if (!trackedDraftIdRef.current) {
      if (liveDraftId) trackedDraftIdRef.current = liveDraftId;
      return;
    }
    const closed = closedDraftOf(savedRef.current, trackedDraftIdRef.current, meKey);
    if (!closed) return;
    const lost = lostFilingRef.current;
    if (closed.savedByMe && lost && lowerId(lost.draftId) === lowerId(closed.draftId)) {
      finishFiled({ already_saved: true, message: `You saved Log Work for this job ${logWorkAtWhen(closed.savedAt)}.` }, lost.carryOn)
        .catch(() => {});
      return;
    }
    setClosedDraft(closed);
    // Runs when the saved Log Work's draft or last save changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveDraftId, liveLastSavedId, liveReadyStatus]);

  /**
   * Complete Work Log, or (parkForHome) Finish at home: every check a
   * completion needs runs, bar the last Job Stages time, then the Log Work is
   * kept as saved Log Work instead of being filed.
   */
  const handleSubmit = async (event, { parkForHome = false } = {}) => {
    event.preventDefault();
    submittingRef.current = true;
    setSaving(true);
    setError(null);
    setNotice(null);

    try {
      // A Complete Work Log whose reply was lost goes again first, exactly as
      // it was sent: the server files it once, or answers already_saved. Not
      // when something was changed since (the checks below then find out).
      const lost = live && !parkForHome ? lostFilingRef.current : null;
      if (lost && !closedDraft && !hasUnsentChanges()) {
        await sendFiling(lost.body, { draftId: lost.draftId, carryOnSent: lost.carryOn });
        return;
      }

      // The sections in job order: their state as last reported, each with
      // the robot's sign-off for this visit (none on the sales task section).
      const sections = checkSections
        .map(section => {
          const state = checkSectionStateRef.current[section.key];
          if (!state) return null;
          const isRobot = section.key !== 'task' && Boolean(section.asset.asset_id);
          return {
            ...state,
            isRobot,
            robotOutcome: isRobot ? robotOutcomeFor(section.key) : null
          };
        })
        .filter(Boolean);
      const robotCount = checkSections.length;
      // With several robots, say which one a check-form message is about.
      const robotPrefix = (section) => (robotCount > 1 && section.assetRef ? `${section.assetRef} - ` : '');
      // A robot marked Not done on a closing log needs no form, only a reason.
      const skipsForm = (section) => closesWork && section.isRobot && section.robotOutcome.outcome === 'not_done';

      if (sections.some(section => section.showWarning && !skipsForm(section))) {
        throw new Error('Check template targeting is ambiguous - an admin must fix it in Settings before this work can be logged.');
      }
      if (isSiteWork) {
        const vanCheckError = validateDailyVanCheck(dailyVanCheck);
        if (vanCheckError) throw new Error(vanCheckError);
        const crewIds = vanCrew.map(engineer => engineer.user_id);
        if (seatedIds.some(seatedId => !crewIds.includes(seatedId))) {
          throw new Error('Only engineers who were on this job can be recorded in the van. Add them to the job first.');
        }
        if (needsSecondVan) {
          if (!secondVan.vehicle_id) {
            throw new Error(`There are ${vanCrew.length} engineers on this job but the van seats ${seatCount}. Choose the second van.`);
          }
          if (!secondVan.driver_id) {
            throw new Error('Choose which engineer drove the second van - they complete its check.');
          }
          if (secondVanCheckOpen) {
            const secondVanError = validateDailyVanCheck(secondVanCheck);
            if (secondVanError) throw new Error(`Second van: ${secondVanError}`);
          }
        }
      }
      // A handover completes this log and passes the baton: compulsory items
      // are waived (partial check answers are saved) but the notes ARE the
      // handover, so they are required (Gareth 2026-08-12). Closing still
      // demands the full check form. Carry on tomorrow needs them too.
      if (!closesWork && !formData.notes.trim()) {
        throw new Error(carryOn
          ? CARRY_ON_NOTES_NEEDED
          : 'Handover notes are required — write what the next owner needs to know in the Notes box.');
      }
      // Every Job Stages time is needed before a site log completes: a close,
      // a hand over and Carry on tomorrow alike (Gareth 2026-10-07 and
      // 2026-10-12). Finish at home needs the first three. Said here in the
      // server's own words, before anything is sent.
      if (isSiteWork) {
        if (!journeyLoaded) throw new Error(JOB_STAGES_STILL_LOADING_MESSAGE);
        const stagesProblem = jobStagesProblem(travelRecord, { waitingForLast: parkForHome });
        if (stagesProblem) throw new Error(stagesProblem);
      }
      if (earlierDayBlocks) throw new Error(earlierDaySentence);
      if (closesWork) {
        for (const section of sections) {
          if (skipsForm(section)) {
            if (!section.robotOutcome.note.trim()) {
              throw new Error(`Say why ${section.assetRef || 'the robot'} was not done.`);
            }
            continue;
          }
          if (!section.useCheckTemplate) continue;
          const validation = validateAnswers(section.template.schema_json, section.answers, { requireComplete: true, attachments: section.attachments });
          if (!validation.valid) {
            const firstError = validation.errors[0];
            throw new Error(`${robotPrefix(section)}${section.template.name || 'Check'}: ${firstError.fieldLabel ? `${firstError.fieldLabel} - ` : ''}${firstError.message}`);
          }
          if (!section.completed) {
            throw new Error(`${robotPrefix(section)}Please confirm the ${section.template.name || 'check'} is complete before saving.`);
          }
        }
      }

      // Parts used (spec 6.4), with the server's own sentences. Only while
      // the section is on screen: when saved Log Work is not available the
      // lines cannot be changed, and check 3 says so instead.
      if (storeUp) {
        // 1. A quantity box holding text that cannot be read: never file the
        //    old quantity silently. Only lines still shown count, so a line a
        //    colleague removed can never block the save.
        const shownKeys = new Set(spareLineEntries.map(entry => entry.key));
        const textProblem = Object.entries(spareTextProblemsRef.current)
          .filter(([key, sentence]) => sentence && shownKeys.has(key))
          .map(([, sentence]) => sentence)[0];
        if (textProblem) throw new Error(textProblem);
        // 2. The line checks, on the raw draft values (spareValueProblem).
        const spareProblem = firstSpareProblem(spareLineEntries.map(entry => entry.value), {
          robotIds: new Set(spareRobots.map(robot => robot.asset_id)),
          severalRobots: spareRobots.length > 1
        });
        if (spareProblem) throw new Error(spareProblem);
      }
      // 3. Saved Log Work went unavailable after the window opened (a 503, or
      //    any failed read): a filing then carries no draft id and records no
      //    lines, and the phone copy is cleared once it is filed. Checked
      //    again after the flush below, which can lose the store too.
      if (live && !storeUp && holdsSpareLines(savedRef.current)) throw new Error(SPARE_PARTS_UNAVAILABLE_MESSAGE);

      if (live && !storeUp && formData.assign_next_type === 'carry_on') {
        throw new Error(CARRY_ON_UNAVAILABLE_MESSAGE);
      }
      // Finish at home: it would complete but for the last Job Stages time.
      // Nothing is filed; it stays as saved Log Work for Today to finish.
      if (parkForHome) {
        if (closedDraft) throw new Error(closedDraft.message);
        await flushAndClose(FINISH_AT_HOME_SAVED_MESSAGE);
        return;
      }

      // Saved Log Work, in order (spec 6.4): send everything still on this
      // phone, check nobody changed a part since, then file exactly the
      // versions and files the screen has.
      let filing = { draftId: undefined, versions: undefined, fileIds: undefined };
      let attachments = formData.attachments;
      let vanCheck = dailyVanCheck;
      let secondCheck = secondVanCheck;
      let latest = null;
      let checkPhotos = null;
      if (live) {
        if (closedDraft) throw new Error(closedDraft.message);
        const flushed = await savedRef.current.flush();
        if (!flushed.unavailable) {
          if (!flushed.ok) {
            // A change refused because the Log Work was saved or cleared:
            // read it again and stop this window rather than file without it.
            if (flushed.stale) {
              try { await savedRef.current.refresh(); } catch (err) { /* the sentence still shows */ }
              if (await stopIfClosed()) return;
            }
            throw new Error(flushed.message || SEND_FAILED_MESSAGE);
          }
          const refreshed = await savedRef.current.refresh();
          if (refreshed.offline) throw new Error(NO_SIGNAL_SAVE_MESSAGE);
          if (await stopIfClosed()) return;
          if (Array.isArray(refreshed.changed) && refreshed.changed.length > 0) {
            throw new Error(refreshed.message || 'A colleague changed this Log Work. Check it, then save again.');
          }
          if (!refreshed.ok && !refreshed.unavailable) {
            throw new Error(refreshed.message || 'Log Work could not be checked. Try again.');
          }
        }
        await afterRender();
        latest = savedRef.current;
        if (latest.status === 'offline' || latest.status === 'loading') throw new Error(NO_SIGNAL_SAVE_MESSAGE);
        if (latest.status === 'ready') {
          // A window that opened on a saved Log Work never files without it.
          if (trackedDraftIdRef.current && lowerId(latest.draftId) !== lowerId(trackedDraftIdRef.current)) {
            if (await stopIfClosed()) return;
            throw new Error('This Log Work was saved or cleared on another phone. Close Log Work and open it again.');
          }
          if (!trackedDraftIdRef.current && latest.draftId) trackedDraftIdRef.current = latest.draftId;
        }
        if (latest.status === 'ready' && latest.draftId) {
          filing = {
            draftId: latest.draftId,
            versions: latest.versionsForFiling(),
            fileIds: latest.fileIdsForFiling()
          };
        }
        if (latest.status === 'unavailable') {
          // Parts used lines are never filed without saved Log Work (check 3
          // above, again: the store can go away during the flush itself).
          if (holdsSpareLines(latest)) throw new Error(SPARE_PARTS_UNAVAILABLE_MESSAGE);
          // The store went away after the window opened: photos still on the
          // phone go with their content, as they did before saved Log Work.
          const content = await queuedPhotoContent(latest);
          const withContent = (list) => (Array.isArray(list) ? list : [])
            .map(item => (item && item.client_ref && !item.url && !item.dataUrl && content.has(item.client_ref)
              ? { ...item, ...content.get(item.client_ref) }
              : item))
            .filter(item => item && (item.url || item.dataUrl || item.data));
          attachments = [
            ...(Array.isArray(formData.attachments) ? formData.attachments : []),
            ...latest.attachmentsFor('attachments'),
            ...(latest.photos || [])
              .filter(photo => photo.part_key === 'attachments' && content.has(photo.client_ref))
              .map(photo => content.get(photo.client_ref))
          ];
          vanCheck = {
            ...dailyVanCheck,
            damage_photo_attachments: withContent(linkSentAttachments(dailyVanCheck.damage_photo_attachments || [], latest.files))
          };
          secondCheck = {
            ...secondVanCheck,
            damage_photo_attachments: withContent(linkSentAttachments(secondVanCheck.damage_photo_attachments || [], latest.files))
          };
          checkPhotos = (check) => withContent(linkSentAttachments(Array.isArray(check.attachments) ? check.attachments : [], latest.files));
        } else {
          // Photos travel as links to the files already in the saved Log
          // Work: the screen's, plus any a colleague added that the screen
          // does not hold yet (the server files only what the body carries).
          const filingStore = latest;
          attachments = filingStore.attachmentsFor('attachments');
          const sentOf = (partKey, withStep) => filingStore.attachmentsFor(partKey)
            .map(file => attachmentFromFileRef(file, withStep ? (file.step_key || '') : undefined));
          vanCheck = {
            ...dailyVanCheck,
            damage_photo_attachments: withSavedFiles(dailyVanCheck.damage_photo_attachments, filingStore.files, sentOf('van_check'))
          };
          secondCheck = {
            ...secondVanCheck,
            damage_photo_attachments: withSavedFiles(secondVanCheck.damage_photo_attachments, filingStore.files, sentOf('second_van'))
          };
          checkPhotos = (check) => {
            const section = checkSections.find(item => lowerId(item.asset.asset_id) === lowerId(check.asset_id));
            const partKey = section ? partKeyOfSection(section.key) : null;
            const part = partKey && filingStore.parts ? filingStore.parts[partKey] : null;
            const sameForm = Boolean(part && part.value && part.value.form && check.template_id
              && part.value.form.template_id === check.template_id);
            return withSavedFiles(check.attachments, filingStore.files, sameForm ? sentOf(partKey, true) : []);
          };
        }
      }

      const body = buildLogWorkPayload({
        formData: { ...formData, attachments },
        sections,
        closesWork,
        followUpPerRobot,
        isSiteWork,
        dailyVanCheck: vanCheck,
        travelRecord,
        additionalEngineerIds,
        needsSecondVan,
        secondVan,
        secondVanCheckOpen,
        secondVanCheck: secondCheck,
        draftId: filing.draftId,
        carryOn,
        versions: filing.versions,
        fileIds: filing.fileIds
      });
      if (latest && checkPhotos && Array.isArray(body.template_checks)) {
        body.template_checks = body.template_checks.map(check => ({ ...check, attachments: checkPhotos(check) }));
      }

      await sendFiling(body, { draftId: filing.draftId, carryOnSent: carryOn });
    } catch (err) {
      setError(err.message);
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  /**
   * Sends what is on this phone, then closes. Nothing is filed. A change
   * that could not be sent is kept on the phone and its sentence shown on
   * the job page; otherwise savedNotice is.
   */
  const flushAndClose = async (savedNotice = null) => {
    let result = null;
    try {
      result = await savedRef.current.flush();
    } catch (err) {
      result = { ok: false, message: err.message };
    }
    setSaving(false);
    const message = result && !result.ok && !result.unavailable ? result.message : null;
    if (onSavedForLater) onSavedForLater(message || savedNotice);
    else onClose();
  };

  // Save Progress (the main button while no outcome is chosen).
  const saveProgress = async () => {
    setSaving(true);
    await flushAndClose();
  };

  const chooseConflict = async (conflict, choice) => {
    const result = await savedRef.current.resolveConflict(conflict.partKey, choice);
    if (result && result.message) setNotice(result.message);
    if (choice !== 'mine') {
      await afterRender();
      applyServerParts([conflict.partKey], { includeLocal: choice === 'both' });
    }
  };
  const [shownStale, setShownStale] = useState({});
  const chooseStale = async (copy, choice) => {
    const result = await savedRef.current.resolveStale(copy.id, choice);
    // Parts used lines are copied under their own keys (spec 6.5), so a line
    // already recorded is never recorded twice: said once, here.
    if (choice === 'copy' && result && Array.isArray(result.copiedSpare) && result.copiedSpare.length > 0) {
      setNotice(spareCopiedNotice(result.copiedSpare));
    }
    if (!result || result.ok === false) {
      if (result && result.message) setError(result.message);
      return;
    }
    if (choice === 'show') setShownStale(prev => ({ ...prev, [copy.id]: result }));
    if (choice === 'copy') {
      await afterRender();
      applyServerParts(copy.parts.map(part => part.partKey), { includeLocal: true });
    }
  };
  const discardFailedItem = async (item) => {
    if (item.kind === 'part') {
      await savedRef.current.discardFailed(item.key);
      await afterRender();
      applyServerParts([item.key]);
    } else {
      // A failed photo leaves the van checks and its check form too, so it is
      // never filed as a photo with no file.
      const photo = ((savedRef.current && savedRef.current.photos) || []).find(entry => entry.client_ref === item.key);
      const partKey = photo ? photo.part_key : null;
      await savedRef.current.removeFile(item.key);
      const withoutRef = (list) => (Array.isArray(list) ? list.filter(entry => !entry || entry.client_ref !== item.key) : list);
      setDailyVanCheck(prev => ({ ...prev, damage_photo_attachments: withoutRef(prev.damage_photo_attachments) }));
      const check = { ...secondVanRef.current.check, damage_photo_attachments: withoutRef(secondVanRef.current.check.damage_photo_attachments) };
      secondVanRef.current = { ...secondVanRef.current, check };
      setSecondVanCheck(check);
      if (partKey && isFormPartKey(partKey)) {
        await afterRender();
        applyRemoteFiles([partKey]);
      }
    }
  };
  const staleValueText = (part) => {
    const value = part.value;
    if (isSparePartKey(part.partKey)) {
      return value && value.name ? `${value.name} ×${quantityText(value.quantity)}` : 'removed';
    }
    if (value && typeof value.notes === 'string') return value.notes || '(empty)';
    return part.changedAt ? `changed ${whenText(part.changedAt)}` : 'changed';
  };

  // Everyone who changed a check form part but is not on the crew: named,
  // with a "was not on this visit" tick (saved to not_on_visit_ids).
  const crewKeys = new Set([meKey, ...additionalEngineerIds.map(lowerId)]);
  const missingWorkers = [];
  if (live) {
    Object.entries(saved.parts).forEach(([key, part]) => {
      if (!isFormPartKey(key) || !part || !part.value || part.carried || !part.changed_by) return;
      const workerId = lowerId(part.changed_by);
      if (crewKeys.has(workerId) || missingWorkers.some(worker => worker.id === workerId)) return;
      missingWorkers.push({ id: workerId, name: part.changed_by_name || 'A colleague', label: partLineLabel(key, sectionAssetRefs) });
    });
  }

  // Saved Log Work: the photos and files in it, sent or still on the phone.
  const attachmentRows = live ? [
    ...saved.files.filter(file => file.part_key === 'attachments')
      .map(file => ({ id: file.file_id, name: file.name || 'File', state: 'sent' })),
    ...saved.photos.filter(photo => photo.part_key === 'attachments')
      .map(photo => ({ id: photo.client_ref, name: photo.name || 'Photo', state: photo.state, message: photo.fail_message }))
  ] : [];

  let statusLine = null;
  let statusWarning = false;
  if (live) {
    const state = saved.saveState || {};
    if (state.message) {
      statusLine = state.message;
      statusWarning = true;
    } else if (state.sending) {
      statusLine = 'Sending...';
    } else if (state.lastSavedAt) {
      statusLine = `Saved ${formatTime(state.lastSavedAt)}`;
    } else if (saved.draft && saved.draft.has_content && saved.draft.last_changed_at) {
      statusLine = `Saved ${whenText(saved.draft.last_changed_at)}`;
    }
  }

  const topStages = (
    <JobStagesCard
      authFetch={authFetch}
      taskId={taskId}
      travelRecord={travelRecord}
      onTravelRecordChange={live ? handleTravelRecordChange : setTravelRecord}
      visibleStages={['departed', 'arrived']}
      sectionTitle="Job Stages — start"
      sectionHint="Stamp as they happen - your live status and location update immediately. Times can be entered manually if you had no signal."
      {...(live ? {
        // A restored journey is read by its id (above), not as "today's
        // latest record", which may be another trip or another day's.
        skipLoad: restoredJourney,
        previousTrip,
        onPreviousTripChange: handlePreviousTrip,
        waitingForRecord: !journeyLoaded
      } : {})}
    />
  );
  const bottomStages = (
    <JobStagesCard
      authFetch={authFetch}
      taskId={taskId}
      travelRecord={travelRecord}
      onTravelRecordChange={live ? handleTravelRecordChange : setTravelRecord}
      visibleStages={['departed_site', 'returned']}
      skipLoad
      {...(live ? { waitingForRecord: !journeyLoaded } : {})}
      sectionTitle="Job Stages — finish"
      sectionHint={FINISH_STAGES_HINT}
    />
  );

  return (
    <div className="modal-overlay">
      <div className="modal-content work-log-modal" onClick={event => event.stopPropagation()}>
        <div className="modal-header">
          <h2>Log Work</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="modal-error">{error}</div>}
            {live && closedDraft && (
              <div className="modal-hint warning saved-log-work-closed" role="alert">{closedDraft.message}</div>
            )}
            {live && notice && <div className="modal-hint" role="status">{notice}</div>}
            {live && saved.status === 'offline' && (
              <div className="modal-hint warning" role="status">{OFFLINE_OPEN_MESSAGE}</div>
            )}
            {live && saved.phoneMessage && <div className="modal-hint">{saved.phoneMessage}</div>}
            {live && saved.conflicts.map(conflict => (
              <div key={`conflict:${conflict.partKey}`} className="modal-hint warning saved-log-work-choice" role="alert">
                {conflict.kind === 'work_type' ? (
                  <>
                    <span>{conflict.serverMessage || conflict.message}</span>
                    <button type="button" className="btn-small" onClick={() => chooseConflict(conflict, 'mine')}>Change it anyway</button>
                    <button type="button" className="btn-small secondary" onClick={() => chooseConflict(conflict, 'theirs')}>Keep the work type</button>
                  </>
                ) : (
                  <>
                    <span>{conflict.message}</span>
                    <button type="button" className="btn-small" onClick={() => chooseConflict(conflict, 'theirs')}>
                      Use {firstNameOf(conflict.theirName)}&apos;s
                    </button>
                    <button type="button" className="btn-small secondary" onClick={() => chooseConflict(conflict, 'mine')}>Keep mine</button>
                    {conflict.canKeepBoth && (
                      <button type="button" className="btn-small secondary" onClick={() => chooseConflict(conflict, 'both')}>Keep both</button>
                    )}
                  </>
                )}
              </div>
            ))}
            {live && saved.staleCopies.map(copy => (
              <div key={`stale:${copy.id}`} className="modal-hint warning" role="alert">
                <div className="saved-log-work-choice">
                  <span>{copy.message}</span>
                  <button type="button" className="btn-small secondary" onClick={() => chooseStale(copy, 'show')}>Show</button>
                  <button type="button" className="btn-small" onClick={() => chooseStale(copy, 'copy')}>Copy into today&apos;s Log Work</button>
                  <button type="button" className="btn-small secondary" onClick={() => chooseStale(copy, 'discard')}>Discard</button>
                </div>
                {shownStale[copy.id] && (
                  <ul className="saved-log-work-lines">
                    {shownStale[copy.id].parts.map(part => (
                      <li key={part.partKey} className="saved-log-work-line">{part.label}: {staleValueText(part)}</li>
                    ))}
                    {shownStale[copy.id].photos.map(photo => (
                      <li key={photo.client_ref} className="saved-log-work-line">Photo: {photo.name || 'unnamed'}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
            {live && saved.failed.map(item => (
              <div key={`failed:${item.kind}:${item.key}`} className="modal-hint warning saved-log-work-choice" role="alert">
                <span>{item.label}: {item.message}</span>
                <button type="button" className="btn-small secondary" onClick={() => discardFailedItem(item)}>
                  {item.kind === 'part' ? 'Discard' : 'Remove'}
                </button>
              </div>
            ))}
            {live && earlierDaySentence && (
              <div className="modal-hint warning" role="note">
                {earlierDaySentence}
                <label className="saved-log-work-choice">
                  <input
                    type="checkbox"
                    checked={notWorkedToday}
                    onChange={event => setNotWorkedToday(event.target.checked)}
                  />
                  {' '}I did not work on this job today
                </label>
              </div>
            )}
            <div className="form-group">
              <label>Work type *</label>
              <select name="log_type" value={formData.log_type} onChange={handleChange} required>
                {(config.work_types || []).map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Work Period *</label>
              <select name="work_period" value={formData.work_period} onChange={handleChange} required>
                {(config.work_periods || []).map(period => (
                  <option key={period.value} value={period.value}>{period.label}</option>
                ))}
              </select>
            </div>
            {!isSiteWork && (
              <>
                <div className="form-group">
                  <label>Start time *</label>
                  <input type="datetime-local" name="started_at" value={formData.started_at} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Finish time *</label>
                  <input type="datetime-local" name="ended_at" value={formData.ended_at} onChange={handleChange} required />
                </div>
              </>
            )}
            {isSiteWork && (
              <div className="modal-hint stage-times-note">
                Times come from Job Stages: started {stageStampTime(travelRecord?.departed_at)} — finished {stageStampTime(travelRecord?.returned_at || travelRecord?.departed_site_at)}
              </div>
            )}

            {/* Start stamps live at the TOP — tapped before the long check
                forms; the finish stamps sit next to the Outcome below
                (Gareth 2026-08-12). */}
            {isSiteWork && (live ? <div onClickCapture={markStampIntent}>{topStages}</div> : topStages)}

            <div className="form-group">
              <label>Notes</label>
              <textarea name="notes" value={formData.notes} onChange={handleChange} rows={4} />
            </div>

            {/* Other engineers on this job (Phase 4). Each gets their OWN
                work log with these times, so all the time on the job is
                captured and each is paid on their own rates. */}
            {(coEngineerOptions.length > 0 || additionalEngineerIds.length > 0 || missingWorkers.length > 0) && (
              <div className="form-group">
                <label htmlFor="task_co_engineer_picker">Other engineers on this job</label>
                {live && (
                  <div className="saved-crew-me">
                    <label>
                      <input type="checkbox" checked readOnly disabled />
                      {' '}Me{authorIsMe ? authorMark : ''}
                    </label>
                  </div>
                )}
                <EngineerPicker
                  id="task_co_engineer_picker"
                  options={pickerOptions}
                  selectedIds={additionalEngineerIds}
                  onChange={changeCrew}
                  knownNames={pickerNames}
                />
                {droppedCrewNotice && <div className="modal-hint warning">{droppedCrewNotice}</div>}
                {missingWorkers.map(worker => (
                  <div key={worker.id} className="saved-crew-missing">
                    <span className="field-help">{worker.name} filled in {worker.label} but is not named on this Log Work.</span>
                    <label>
                      <input
                        type="checkbox"
                        checked={notOnVisitIds.includes(worker.id)}
                        onChange={event => toggleNotOnVisit(worker.id, event.target.checked)}
                      />
                      {' '}{worker.name} was not on this visit
                    </label>
                  </div>
                ))}
                <span className="field-help">
                  {prefilledFromBooking && additionalEngineerIds.length > 0 && 'Filled in from who is booked on this job. Take off anyone who was not there. '}
                  {additionalEngineerIds.length > 0
                    ? `${additionalEngineerIds.length + 1} engineers on this job — the others each get their own hours to confirm, with these times, which they can adjust if they finished at a different time.`
                    : 'Optional. Anyone else who worked this job gets their own hours to confirm, with these times.'}
                </span>
              </div>
            )}

            <div className="form-group">
              <label>Attachments</label>
              <input type="file" multiple onChange={handleChange} />
              {live && attachmentRows.length > 0 && (
                <ul className="saved-log-work-files" aria-label="Photos and files in this Log Work">
                  {attachmentRows.map(row => (
                    <li key={row.id}>
                      <span className="saved-log-work-file-name">{row.name}</span>
                      <span className={`saved-log-work-file-state ${row.state}`}>
                        {row.state === 'failed' ? `Failed: ${row.message || 'not sent'}` : (PHOTO_STATE_WORDS[row.state] || '')}
                      </span>
                      <button
                        type="button"
                        className="btn-small secondary"
                        aria-label={`Remove ${row.name}`}
                        onClick={() => savedRef.current.removeFile(row.id)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {isSiteWork && (
              <DailyVanCheckSection
                authFetch={authFetch}
                value={dailyVanCheck}
                onChange={live ? changeVanCheck : setDailyVanCheck}
                engineers={vanCrew}
                required={isSiteWork}
                onError={setError}
                {...(storeUp ? { uploadAttachment: uploaderFor('van_check'), removeUploadedAttachment: removeUploaded } : {})}
              />
            )}

            {/* More engineers than seats: a second van, checked by whoever
                drove it. The server refuses the save without it, so it is
                asked for here rather than as a refusal on site. */}
            {needsSecondVan && (
              <div className="second-van-panel" role="group" aria-label="Second van">
                <div className="modal-hint warning">
                  {vanCrew.length} engineers on this job, and {selectedVehicle?.registration_number || 'this van'} seats {seatCount}.
                  {' '}Choose a second van for the other {vanCrew.length - seatCount}.
                </div>
                <div className="form-group">
                  <label htmlFor="task_second_van_vehicle">Second van *</label>
                  <select
                    id="task_second_van_vehicle"
                    value={secondVan.vehicle_id}
                    onChange={event => chooseSecondVan(event.target.value)}
                  >
                    <option value="">Select a van...</option>
                    {vehicles
                      .filter(vehicle => vehicle.vehicle_id !== dailyVanCheck.vehicle_id)
                      .map(vehicle => (
                        <option key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                          {vehicle.registration_number}{vehicle.number_of_seats ? ` (${vehicle.number_of_seats} seats)` : ''}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="task_second_van_driver">Who drove it *</label>
                  <select
                    id="task_second_van_driver"
                    value={secondVan.driver_id}
                    onChange={event => chooseSecondVanDriver(event.target.value)}
                  >
                    <option value="">Select the driver...</option>
                    {secondVanCrew.map(engineer => (
                      <option key={engineer.user_id} value={engineer.user_id}>{engineer.full_name}</option>
                    ))}
                  </select>
                  <span className="field-help">
                    They complete the second van&apos;s check - unless they already checked that van earlier today.
                  </span>
                </div>
                {secondVan.driver_id && (
                  <div className="form-group">
                    <label htmlFor="task_second_van_check_open">
                      <input
                        id="task_second_van_check_open"
                        type="checkbox"
                        checked={secondVanCheckOpen}
                        onChange={event => openSecondVanCheck(event.target.checked)}
                      />
                      {' '}Fill in {secondVanDriverName}&apos;s check of the second van now
                    </label>
                  </div>
                )}
                {secondVan.driver_id && secondVanCheckOpen && (
                  <div className="second-van-check" role="group" aria-label="Second van check">
                    <DailyVanCheckSection
                      authFetch={authFetch}
                      value={secondVanCheck}
                      onChange={changeSecondVanCheck}
                      engineers={secondVanCrew}
                      required
                      onError={setError}
                      {...(storeUp ? { uploadAttachment: uploaderFor('second_van'), removeUploadedAttachment: removeUploaded } : {})}
                    />
                  </div>
                )}
              </div>
            )}

            {checkSections.map(section => (
              <TaskAssetCheckSection
                key={`${section.key}:${sectionVersions[section.key] || 0}`}
                sectionKey={section.key}
                authFetch={authFetch}
                task={task}
                asset={section.asset}
                workLogType={formData.log_type}
                taskTypeLabel={taskTypeLabel}
                robotCount={checkSections.length}
                robotOutcome={section.key === 'task' ? null : robotOutcomeFor(section.key)}
                onRobotOutcomeChange={section.key === 'task' ? null : (patch) => patchRobotOutcome(section.key, patch)}
                showRobotOutcome={closesWork && section.key !== 'task'}
                onError={setError}
                onStateChange={handleCheckSectionState}
                {...(live ? {
                  savedSection: savedSectionFor(section.key),
                  onPartChange: handlePartChange
                } : {})}
                {...(storeUp ? {
                  uploadAttachment: uploaderFor(partKeyOfSection(section.key)),
                  removeUploadedAttachment: removeUploaded
                } : {})}
              />
            ))}

            {live && referenceSections.length > 0 && (
              <div className="saved-log-work-reference" role="group" aria-label="Kept for reference">
                <div className="saved-log-work-subheading">Kept for reference</div>
                {referenceSections.map(item => (
                  <TaskAssetCheckSection
                    key={item.id}
                    sectionKey={item.id}
                    authFetch={authFetch}
                    task={task}
                    asset={item.asset}
                    workLogType={(item.form && item.form.log_type) || formData.log_type}
                    taskTypeLabel={taskTypeLabel}
                    robotCount={checkSections.length}
                    robotOutcome={null}
                    onRobotOutcomeChange={null}
                    showRobotOutcome={false}
                    onError={setError}
                    onStateChange={handleCheckSectionState}
                    savedSection={{ form: item.form, files: [] }}
                    readOnlyReason={item.reason}
                  />
                ))}
              </div>
            )}

            {/* Finish stamps sit next to the Outcome — stamped at the same
                real-world moment (Gareth 2026-08-12). */}
            {isSiteWork && (live ? <div onClickCapture={markStampIntent}>{bottomStages}</div> : bottomStages)}

            {live && selfTakenBy && (
              <div className="modal-hint warning" role="note">
                {selfTakenBy} chose to take the job back. Choose who takes it next.
              </div>
            )}

            <WorkLogOutcomeSection
              config={config}
              engineers={engineers}
              summaryLabel="Completion summary"
              followUpPlaceholder={`Fault found during ${task?.task_ref || 'this job'}`}
              notesProvided={Boolean(formData.notes.trim())}
              robots={jobRobots.map(robot => ({ asset_id: robot.asset_id, asset_ref: robot.asset_ref }))}
              followUpPerRobot={followUpPerRobot}
              offerCarryOn={storeUp}
              // While Log Work can be saved part-way, no outcome is "In
              // Progress", as on a ticket. Without that it stays compulsory.
              allowInProgress={storeUp}
              value={{
                outcome: formData.outcome,
                summary: formData.completion_summary,
                follow_up_ticket_required: formData.follow_up_ticket_required,
                follow_up_subject: formData.follow_up_subject,
                follow_up_description: formData.follow_up_description,
                follow_up_priority: formData.follow_up_priority,
                follow_up_asset_id: formData.follow_up_asset_id,
                assign_next_type: formData.assign_next_type,
                assigned_to_next_id: formData.assigned_to_next_id
              }}
              onChange={(patch) => applyForm({
                ...('summary' in patch ? { completion_summary: patch.summary } : {}),
                ...Object.fromEntries(Object.entries(patch).filter(([key]) => key !== 'summary'))
              })}
            />

            {/* Parts used (spare parts, spec 6.4): saved as you go in saved
                Log Work, with no signal too, and recorded only when Log Work
                is saved. Usable before an outcome is chosen. Never inside a
                .form-group (its 14px inputs make iPhones zoom). */}
            {storeUp && !isSalesActionTask && canRecordParts && (
              <div className="form-section spare-parts-section">
                <div className="form-section-header compact">
                  <h3>Parts used</h3>
                  <p>Add each part fitted on this visit. Parts are recorded when Log Work is saved.</p>
                </div>
                <SparePartsUsed
                  id="task-spare-parts"
                  lines={spareLineEntries}
                  robots={spareRobots}
                  catalogue={spareParts}
                  nameOf={nameOfUser}
                  showAddedBy
                  disabled={Boolean(closedDraft) || saving}
                  onAdd={addSpareLine}
                  onChange={(key, value) => persist(key, value)}
                  onRemove={(key) => persist(key, null)}
                  onQuantityProblem={(key, sentence) => { spareTextProblemsRef.current[key] = sentence || undefined; }}
                />
              </div>
            )}
            {live && !storeUp && !isSalesActionTask && canRecordParts && spareLineEntries.length > 0 && (
              <div className="modal-hint warning" role="note">{SPARE_PARTS_LOCKED_MESSAGE}</div>
            )}
          </div>
          <div className="modal-footer" style={live ? { flexWrap: 'wrap' } : undefined}>
            {live && statusLine && (
              <div className={`saved-log-work-status${statusWarning ? ' warning' : ''}`} role="status">{statusLine}</div>
            )}
            {/* The footer works as the ticket window's does (Gareth
                2026-10-12): one main button that follows the Outcome. Saved
                Log Work is kept as it is typed, so nothing is thrown away by
                leaving: the first button is Close, not Cancel. */}
            <button type="button" className="btn-cancel" onClick={onClose}>{live ? 'Close' : 'Cancel'}</button>
            {/* Only while the last Job Stages time is still to come: once it
                is recorded, Complete Work Log is the way. */}
            {storeUp && isSiteWork && hasOutcome && !travelRecord?.returned_at && (
              <button
                type="button"
                className="btn-secondary"
                disabled={saving || Boolean(closedDraft)}
                onClick={(event) => handleSubmit(event, { parkForHome: true })}
                title={`Save everything now and complete this from Today when you are home or leave for your next job (${ARRIVED_HOME_LABEL})`}
              >
                {saving ? 'Saving...' : 'Finish at home'}
              </button>
            )}
            {storeUp && !hasOutcome ? (
              <button key="save-progress" type="button" className="btn-save" onClick={saveProgress} disabled={saving || Boolean(closedDraft)}>
                {saving ? 'Saving...' : 'Save Progress'}
              </button>
            ) : (
              <button key="complete" type="submit" className="btn-save" disabled={saving || earlierDayBlocks || Boolean(live && closedDraft)}>
                {saving ? 'Saving...' : 'Complete Work Log'}
              </button>
            )}
          </div>
        </form>
      </div>
      {confirmElement}
    </div>
  );
}

export default TaskDetail;
